import React, { useState, useEffect, useRef, useCallback } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { pick } from 'ramda';
import debounce from 'lodash.debounce';
import {
  Drawer,
  TabsContainer,
  TabItem,
  Button,
  TextInputWithLabel,
  TextInput,
  RadioButton,
  PopupMenu,
  PressedButton,
  DatePicker,
  TimePicker,
  FileUploader,
  Loader
} from 'quantux-ui';
// Local imports
import { BASE_URL, getHighlightedText, dataURItoBlob } from '../../utils';
import CaseUserCard from './case-user-card';
import MaskedDataInput from '../masked-data-input';
import CaseField from './case-field';
import FileCard from '../file-card';
import CameraContainer from './camera-container';
import CameraCard from './camera-card';
import CropperContainer from './cropper-container';
import { gray, blue, salmon } from '../../theme';

const bellIcon = (
  <svg
    width="20"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.8 10.461v6.154l2.4 2.462v1.23H0v-1.23l2.4-2.462v-6.153c0-3.791 1.956-6.942 5.4-7.779v-.837C7.8.825 8.604 0 9.6 0s1.8.825 1.8 1.846v.837c3.432.837 5.4 4 5.4 7.778Zm-8.402 6.154h2.4v-2.461h-2.4v2.461Zm2.4-4.923h-2.4V6.77h2.4v4.923Zm1.205 9.847c0 1.353-1.08 2.461-2.4 2.461-1.332 0-2.4-1.108-2.4-2.461h4.8Z"
      fill="#FF6060"
    />
  </svg>
);

// const today = new Date();
const isTouchDevice = true;
const CaseDrawer = (props) => {
  // const context = useContext(ThemeContext);
  // const { isTouchDevice } = context.theme;

  // Case Data props
  const { caseData } = props;
  const {
    _id = '',
    // ---------- Case Drawer Header Area ----------
    dateOfSurgery: caseDateOfSurgery = new Date(),
    patientType: casePatientType = '',
    // ---------- Face Sheet Tab ----------
    patientSex: casePatientGender = '',
    patientDob: casePatientDob = '',
    patientFirstName: casePatientFirstName = '',
    patientMiddleName: casePatientMiddleName = '',
    patientLastName: casePatientLastName = '',
    patientStreetAddress: casePatientStreetAddress = '',
    patientSuite: casePatientSuite = '',
    patientCity: casePatientCity = '',
    patientState: casePatientState = '',
    patientZip: casePatientZip = '',
    // ---------- Anesthesia Forms Tab ----------
    consentFormKey: caseConsentFormKey = '',
    intraOpFormKey: caseIntraOpFormKey = '',
    postOfFormKey: casePostOfFormKey = '',
    preOpFormKey: casePreOpFormKey = '',
    // ---------- Case Details Tab ----------
    facility: caseFacility = {},
    otherFacility: caseOtherFacility = {},
    //
    surgeons: caseSurgeons = [],
    otherSurgeon = [],
    //
    anesthesiologists: caseAnesthesiologists = [],
    otherAnesthesiologist = [],
    isNoneAnesthesiologist: caseIsNoneAnesthesiologist = false,
    //
    crnas: caseCrnas = [],
    otherCrna = [],
    isNoneCrna: caseIsNoneCrna = false,
    //
    surgicalProcedures: caseSurgicalProcedures = [],
    otherSurgicalProcedure = [],
    isOtherSurgicalProcedure: caseIsOtherSurgicalProcedure = false,
    //
    preOpDx: casePreOpDx = [],
    isOtherPreOpDx: caseIsOtherPreOpDx = false,
    //
    anesthesiaProcedures: caseAnesthesiaProcedures = [],
    isOtherAnesthesiaProcedure: caseIsOtherAnesthesiaProcedure = false,
    //
    asaScore: caseAsaScore = '',
    isEmergencyAsaScore: caseIsEmergencyAsaScore = false,
    anesthesiaTypes: caseAnesthesiaTypes = [],
    anesthesiaModifiers: caseAnesthesiaModifiers = [],
    anesthesiaStartDate: caseAnesthesiaStartDate = '',
    anesthesiaEndDate: caseAnesthesiaEndDate = '',
    faceSheetImageData = { images: [], croppedImages: [], activeIndex: 0, isReScan: false },
    consentFormImageData = { images: [], croppedImages: [], activeIndex: 0, isReScan: false },
    preOpFormImageData = { images: [], croppedImages: [], activeIndex: 0, isReScan: false },
    intraOpFormImageData = { images: [], croppedImages: [], activeIndex: 0, isReScan: false },
    postOpFormImageData = { images: [], croppedImages: [], activeIndex: 0, isReScan: false }
  } = caseData;

  // if (process.env.NODE_ENV === 'development') {
  //   console.log(caseData);
  // }

  // ------------------ Processing Case Form Data for use ------------------
  const dateOfSurgery = caseDateOfSurgery.length > 0 ? new Date(Date.parse(caseDateOfSurgery)) : new Date();
  const dateOfBirth = casePatientDob.length > 0 ? new Date(Date.parse(casePatientDob)) : new Date();
  const anesStartDate = caseAnesthesiaStartDate.length > 0 ? new Date(Date.parse(caseAnesthesiaStartDate)) : new Date();
  const anesEndDate = caseAnesthesiaEndDate.length > 0 ? new Date(Date.parse(caseAnesthesiaEndDate)) : new Date();
  let patientGender = '';
  if (casePatientGender.length > 0) {
    if (casePatientGender.toLowerCase().startsWith('m')) {
      patientGender = 'M';
    } else if (casePatientGender.toLowerCase().startsWith('f')) {
      patientGender = 'F';
    }
  }

  // --------------------- Case Drawer State Variables ---------------------
  // Drawer related state variables
  const [activeTab, setActiveTab] = useState(0);
  // Form Header
  const [dosDate, setDosDate] = useState(dateOfSurgery.getDate());
  const [dosMonth, setDosMonth] = useState(dateOfSurgery.getMonth());
  const [dosYear, setDosYear] = useState(dateOfSurgery.getFullYear());
  // Patient
  const [patientType, setPatientType] = useState(casePatientType);
  const [patFirstName, setPatFirstName] = useState(casePatientFirstName);
  const [patMiddleName, setPatMiddleName] = useState(casePatientMiddleName);
  const [patLastName, setPatLastName] = useState(casePatientLastName);
  const [patGender, setPatGender] = useState(patientGender);
  const [patStreetAddress, setPatStreetAddress] = useState(casePatientStreetAddress);
  const [patSuite, setPatSuite] = useState(casePatientSuite);
  const [patCity, setPatCity] = useState(casePatientCity);
  const [patState, setPatState] = useState(casePatientState);
  const [patZip, setPatZip] = useState(casePatientZip);
  const [dobDate, setDobDate] = useState(dateOfBirth.getDate());
  const [dobMonth, setDobMonth] = useState(dateOfBirth.getMonth());
  const [dobYear, setDobYear] = useState(dateOfBirth.getFullYear());
  const [isDobPristine, setIsDobPristine] = useState(!(casePatientDob.length > 0));
  // Facility name
  const [facility, setFacility] = useState(caseFacility || {});
  const [otherFacility, setOtherFacility] = useState(caseOtherFacility);
  const [isFacilityListSearchMenuOpen, toggleFacilityListSearchMenu] = useState(false);
  // Surgeons
  const [surgeonsList, setSurgeonsList] = useState(caseSurgeons);
  const [otherSurgeonsList, setOtherSurgeonsList] = useState(otherSurgeon);
  const [isSurgeonsListSearchMenuOpen, toggleSurgeonsListSearchMenu] = useState(false);
  // Procedures list
  const [proceduresList, setProceduresList] = useState(caseSurgicalProcedures);
  const [otherProceduresList, setOtherProceduresList] = useState(otherSurgicalProcedure);
  const [isProceduresListSearchMenuOpen, toggleProceduresListSearchMenu] = useState(false);
  // Pre Op Dx
  const [isOtherPreOpDx, setIsOtherPreOpDx] = useState(caseIsOtherPreOpDx);
  const [preOpDxList, setPreOpDxList] = useState(casePreOpDx);
  const [isDiagnosisSearchMenuOpen, toggleDiagnosisSearchMenu] = useState(false);
  // Anesthesioloists list
  const [isNoneAnesthesiologist, setIsNoneAnesthesiologist] = useState(caseIsNoneAnesthesiologist);
  const [anesthesiologistsList, setAnesthesiologistsList] = useState(caseAnesthesiologists);
  const [otherAnesthesiologistsList, setOtherAnesthesiologistsList] = useState(otherAnesthesiologist);
  const [isAnesthesiologistsListSearchMenuOpen, toggleAnesthesiologistsListSearchMenu] = useState(false);
  // CRNAs list
  const [isNoneCrna, setIsNoneCrna] = useState(caseIsNoneCrna);
  const [crnasList, setCrnasList] = useState(caseCrnas);
  const [otherCrnasList, setOtherCransList] = useState(otherCrna);
  const [isCrnasListSearchMenuOpen, toggleCrnasListSearchMenu] = useState(false);
  // ASA Score and Emergency switch
  const [asaScore, setAsaScore] = useState(caseAsaScore);
  const [isEmergencyAsaScore, setIsEmergencyAsaScore] = useState(caseIsEmergencyAsaScore);
  // Anesthesia Type
  // const [anesthesiaType, setAnesthesiaType] = useState('');
  const [isGaEtt, setisGaEtt] = useState(caseAnesthesiaTypes.indexOf('GA-ETT') > -1);
  const [isGaLma, setisGaLma] = useState(caseAnesthesiaTypes.indexOf('GA-LMA') > -1);
  const [isMac, setisMac] = useState(caseAnesthesiaTypes.indexOf('MAC') > -1);
  const [isEpidural, setisEpidural] = useState(caseAnesthesiaTypes.indexOf('Epidural') > -1);
  const [isSab, setisSab] = useState(caseAnesthesiaTypes.indexOf('SAB') > -1);
  const [isRegional, setisRegional] = useState(caseAnesthesiaTypes.indexOf('Regional') > -1);
  const [isLocal, setisLocal] = useState(caseAnesthesiaTypes.indexOf('Local') > -1);
  const [isTopical, setisTopical] = useState(caseAnesthesiaTypes.indexOf('Topical') > -1);
  // Anesthesia Modifiers
  // const [anesthesiaModifiers, setAnesthesiaModifiers] = useState('');
  const [isPronePosition, setIsPronePosition] = useState(caseAnesthesiaModifiers.indexOf('PronePosition') > -1);
  const [isFieldAvoidance, setIsFieldAvoidance] = useState(caseAnesthesiaModifiers.indexOf('FieldAvoidance') > -1);
  const [isControlledHypotension, setIsControlledHypotension] = useState(caseAnesthesiaModifiers.indexOf('ControlledHypotension') > -1);
  const [isControlledHypothermia, setIsControlledHypothermia] = useState(caseAnesthesiaModifiers.indexOf('ControlledHypothermia') > -1);
  const [isExtremeAge, setIsExtremeAge] = useState(caseAnesthesiaModifiers.indexOf('ExtremeAge') > -1);
  // Anesthesia Procedures List
  const [isOtherAnesthesiaProcedure, setIsOtherAnesthesiaProcedure] = useState(caseIsOtherAnesthesiaProcedure);
  const [anesthesiaProceduresList, setAnesthesiaProceduresList] = useState(caseAnesthesiaProcedures);
  const [isAnesthesiaProceduresListSearchMenuOpen, toggleAnesthesiaProceduresListSearchMenu] = useState(false);
  const [anesthesiaProceduresListSearchTerm, setAnesthesiaProceduresListSearchTerm] = useState('');
  const [anesthesiaProceduresListSearchResults, setAnesthesiaProceduresListSearchResults] = useState([]);
  // Anesthesia Times (Start)
  const [anesthesiaStartDate, setAnesthesiaStartDate] = useState(anesStartDate.getDate());
  const [anesthesiaStartMonth, setAnesthesiaStartMonth] = useState(anesStartDate.getMonth());
  const [anesthesiaStartYear, setAnesthesiaStartYear] = useState(anesStartDate.getFullYear());
  const [isAnesStartDatePristine, setIsAnesStartDatePristine] = useState(!(caseAnesthesiaStartDate.length > 0));
  const [anesthesiaStartHour, setAnesthesiaStartHour] = useState(anesStartDate.getHours());
  const [anesthesiaStartMinute, setAnesthesiaStartMinute] = useState(anesStartDate.getMinutes());
  const [isAnesthesiaStartTimePristine, setIsAnesthesiaStartTimePristine] = useState(!(caseAnesthesiaStartDate.length > 0));
  // Anesthesia Times (End)
  const [anesthesiaEndDate, setAnesthesiaEndDate] = useState(anesEndDate.getDate());
  const [anesthesiaEndMonth, setAnesthesiaEndMonth] = useState(anesEndDate.getMonth());
  const [anesthesiaEndYear, setAnesthesiaEndYear] = useState(anesEndDate.getFullYear());
  const [isAnesEndDatePristine, setIsAnesEndDatePristine] = useState(!(caseAnesthesiaEndDate.length > 0));
  const [anesthesiaEndHour, setAnesthesiaEndHour] = useState(anesEndDate.getHours());
  const [anesthesiaEndMinute, setAnesthesiaEndMinute] = useState(anesEndDate.getMinutes());
  const [isAnesthesiaEndTimePristine, setIsAnesthesiaEndTimePristine] = useState(!(caseAnesthesiaEndDate.length > 0));
  // Calculated Anesthesia Time
  const [anesthesiaTime, setAnesthesiaTime] = useState(0);
  // Case notes
  const [notes, setNotes] = useState('');
  // Anesthesia forms
  const [isUploadingFaceSheet, setIsUploadingFaceSheet] = useState(false);
  const [showFaceSheetMenu, setShowFaceSheetMenu] = useState(false);
  const [consentFormKey, setConsentFormKey] = useState(caseConsentFormKey);
  const [isConsentFormUploading, setConsentFormUploading] = useState(false);
  const [preOpFormKey, setPreOpFormKey] = useState(casePreOpFormKey);
  const [isPreOpFormUploading, setPreOpFormUploading] = useState(false);
  const [postOpFormKey, setPostOpFormKey] = useState(casePostOfFormKey);
  const [isPostOpFormUploading, setPostOpFormUploading] = useState(false);
  const [intraOpFormKey, setIntraOpFormKey] = useState(caseIntraOpFormKey);
  const [isIntraOpFormUploading, setIntraOpFormUploading] = useState(false);
  // Case data changes
  const [isCaseDataUpdated, setIsCaseDataUpdated] = useState(false);
  // Case Forms (Upload, Crop, Camera, FileDrop etc)
  const [showFaceSheetCamera, setShowFaceSheetCamera] = useState(false);
  const [showConsentFormCamera, setShowConsentFormCamera] = useState(false);
  const [showPreOpFormCamera, setShowPreOpFormCamera] = useState(false);
  const [showPostOpFormCamera, setShowPostOpFormCamera] = useState(false);
  const [showIntraOpFormCamera, setShowIntraOpFormCamera] = useState(false);

  const [showFaceSheetCropper, setShowFaceSheetCropper] = useState(false);
  const [faceSheetCropperEditMode, setFaceSheetCropperEditMode] = useState(false);

  const [showPreOpCropper, setShowPreOpCropper] = useState(false);
  const [preOpCropperEditMode, setPreOpCropeprEditMode] = useState(false);

  const [showPostOpCropper, setShowPostOpCropper] = useState(false);

  const [showIntraOpCropper, setShowIntraOpCropper] = useState(false);
  const [intraOpCropperEditMode, setIntraOpCropeprEditMode] = useState(false);

  const [showConsentFormCropper, setShowConsentFormCropper] = useState(false);
  const [consentFormCropperEditMode, setConsentFormCropperEditMode] = useState(false);

  // const [faceSheetData, setFaceSheetData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [faceSheetData, setFaceSheetData] = useState(faceSheetImageData);
  const [consentFormData, setConsentFormData] = useState(consentFormImageData);
  const [preOpData, setPreOpData] = useState(preOpFormImageData);
  const [postOpData, setPostOpData] = useState(postOpFormImageData);
  const [intraOpData, setIntraOpData] = useState(intraOpFormImageData);
  const [isLoadingFaceSheetImageData, setIsLoadingFaceSheetImageData] = useState(true);
  const [isLoadingConsentFormImageData, setIsLoadingConsentFormImageData] = useState(true);
  const [isLoadingPreOpImageData, setIsLoadingPreOpImageData] = useState(true);
  const [isLoadingIntraOpImageData, setIsLoadingIntraOpImageData] = useState(true);
  // Saving case form
  const [savingCaseForm, setSavingCaseForm] = useState(false);
  // Uploading File / Creating PDF
  const [isUploading, _] = useState(false);

  //  -------------------------------- Refs --------------------------------
  // Case info tab refs
  const facilityRef = useRef();
  const diagnosisRef = useRef();
  const surgeonsRef = useRef();
  const proceduresRef = useRef();
  const anesthesiologistsRef = useRef();
  const crnasRef = useRef();
  const anesthesiaProceduresRef = useRef();
  const anesthesiaProceduresListInputRef = useRef();
  // Case Forms Refs (FaceSheet, Consent Form, Pre, Post & Intra Op Forms)
  const faceSheetFileInputRef = useRef();
  const faceSheetCropper = useRef();
  const faceSheetMenuRef = useRef();
  const consentFormFileInputRef = useRef();
  const consentFormCropper = useRef();
  const preOpFormFileInputRef = useRef();
  const preOpCropper = useRef();
  const postOpFormFileInputRef = useRef();
  const postOpCropper = useRef();
  const intraOpFormFileInputRef = useRef();
  const intraOpCropper = useRef();

  // -------------------------------- Styles -------------------------------
  const styles = StyleSheet.create({
    drawerContainer: {
      background: gray[800],
      color: gray[300],
      // height: '100%',
      // height: `${window.innerHeight}px`,
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      overflow: 'hidden'
    },
    drawerHeader: {
      position: 'relative',
      alignItems: 'left',
      padding: '8px',
      fontWeight: 'bold',
      borderBottom: `1px solid ${gray[600]}`,
      flexShrink: '0',
      background: gray[600]
    },
    headerSvgIcon: {
      fill: gray[300],
      marginRight: '8px',
      height: '20px'
    },
    drawerHeaderCloseIcon: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerContentArea: {
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '48px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    tabs: {
      paddingLeft: '8px',
      borderBottom: `1px solid ${gray[600]}`,
      marginTop: '8px'
    },
    sectionHeading: {
      color: 'white'
    },
    sectionSubHeading: {
      fontWeight: '700'
    },
    requiredHeadingMsg: {
      color: salmon[700],
      fontSize: '12px'
    },
    labelHeading: {
      fontSize: '12px',
      marginBottom: '4px',
      color: gray[200]
    },
    formFieldContainer: {
      position: 'relative',
      lineHeight: '21px'
    },
    anesModBtns: {
      fontSize: '12px'
    },
    notes: {
      fontFamily: 'inherit',
      height: '100%',
      resize: 'none',
      width: '100%',
      background: 'black',
      color: gray[300],
      outline: 'none',
      padding: '4px',
      // borderColor: blue[300],
      border: 'none',
      '::placeholder': {
        color: gray[500]
      },
      ':focus': {
        outline: 'none',
        border: 'none'
      }
    },
    menuItem: {
      cursor: 'pointer',
      background: blue[900],
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      borderBottom: `1px solid ${gray[600]}`,
      fontSize: '14px',
      // height: '40px',
      ':hover': {
        background: gray[500],
        color: 'white'
      }
    },
    menuItemAvatarContainer: {
      width: '32px',
      height: '32px'
    },
    menuItemAvatarImg: {
      // width: 'auto',
      width: '32px',
      height: '32px',
      maxHeight: '32px',
      borderRadius: '50%'
    },
    pinnedMenuItemsContainer: {
      borderTop: `2px solid ${gray[900]}`
    },
    dateWarning: {
      display: 'flex',
      alignItems: 'center',
      color: salmon[500],
      fontSize: '14px'
    },
    headerSection: {
      minWidth: '75px',
      textAlign: 'right',
      fontSize: '12px',
      fontWeight: '700'
    },
    headerSectionText: {
      marginLeft: '4px',
      color: 'white',
      fontSize: '12px',
      fontWeight: '700',
      textTransform: 'capitalize'
    },
    popupMenuContainer: {
      background: '#083050',
      width: '150px',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)'
    },
    // menuItem: {
    //   height: '40px',
    //   display: 'flex',
    //   alignItems: 'center',
    //   cursor: 'pointer',
    //   color: '#5080B0'
    // },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '40px',
      width: '40px'
    },
    menuItemIcon: {
      fontSize: '20px'
    }
  });

  const searchAnesthesiaProceduresList = (value) => {
    const url = new URL(`${BASE_URL}/cpt/searchCpt`);
    // const params = { searchTerm: anesthesiaProceduresListSearchTerm.trim() };
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        toggleAnesthesiaProceduresListSearchMenu(true);
        setAnesthesiaProceduresListSearchResults(data.cptCodes);
      })
      .catch(error => console.log('error', error));
  };

  const debouncedSearchAnesthesiaProceduresList = useCallback(
    debounce((searchTerm) => {
      searchAnesthesiaProceduresList(searchTerm);
    }, 500), []);

  //  -------------------- useEffect hooks Case Info Tab -------------------

  useEffect(() => {
    // const token = localStorage.getItem('token');
    // myHeaders.append('Authorization', `Bearer ${token}`);
    const formData = new FormData();
    formData.append('caseId', _id);
    const url = `${BASE_URL}/cases/caseImagesData`;
    const requestOptions = {
      method: 'POST',
      // headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(url, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          const tmp = { images: [], croppedImages: [], activeIndex: 0, isReScan: false };
          setFaceSheetData(res.faceSheetImageData || tmp);
          setConsentFormData(res.consentFormImageData || tmp);
          setPreOpData(res.preOpFormImageData || tmp);
          setPostOpData(res.postOpFormImageData || tmp);
          setIntraOpData(res.intraOpFormImageData || tmp);
          setIsLoadingFaceSheetImageData(false);
          setIsLoadingConsentFormImageData(false);
          setIsLoadingPreOpImageData(false);
          setIsLoadingIntraOpImageData(false);
        }
      })
      .catch(error => console.log('error', error));
  }, []);

  useEffect(() => {
    if (anesthesiaProceduresListSearchTerm.trim().length > 0) {
      debouncedSearchAnesthesiaProceduresList(anesthesiaProceduresListSearchTerm);
    }
  }, [anesthesiaProceduresListSearchTerm]);

  // Calculating Anesthesia Time
  useEffect(() => {
    const anesthesiaStartDateTime = new Date(
      anesthesiaStartYear,
      anesthesiaStartMonth,
      anesthesiaStartDate,
      anesthesiaStartHour,
      anesthesiaStartMinute
    );

    const anesthesiaEndDateTime = new Date(
      anesthesiaEndYear,
      anesthesiaEndMonth,
      anesthesiaEndDate,
      anesthesiaEndHour,
      anesthesiaEndMinute
    );

    const timeDiff = (anesthesiaEndDateTime.getTime() - anesthesiaStartDateTime.getTime()) / (1000 * 60 * 60);
    setAnesthesiaTime(timeDiff.toFixed(2));
  }, [
    anesthesiaStartDate,
    anesthesiaStartMonth,
    anesthesiaStartYear,
    anesthesiaStartHour,
    anesthesiaStartMinute,
    anesthesiaEndDate,
    anesthesiaEndMonth,
    anesthesiaEndYear,
    anesthesiaEndHour,
    anesthesiaEndMinute
  ]);

  // Set isNoneAnesthesiologist when no anesthesiologist is selected for the case
  useEffect(() => {
    setIsNoneAnesthesiologist(anesthesiologistsList.length === 0 && otherAnesthesiologistsList.length === 0);
  }, [
    anesthesiologistsList,
    otherAnesthesiologistsList
  ]);

  // Set isNoneCrna when no CRNA is selected for the case
  useEffect(() => {
    setIsNoneCrna(crnasList.length === 0 && otherCrnasList.length === 0);
  }, [
    crnasList,
    otherCrnasList
  ]);

  // #region Case Info Tab event handlers
  const handleOnClickInPt = () => {
    setPatientType('InPt');
  };

  const handleOnClickOutPt = () => {
    setPatientType('OutPt');
  };

  const handleOnEditNotes = (e) => {
    const { value } = e.target;
    setNotes(value);
  };

  const handleOnChangeAnesthesiaStartDate = (year, month, date) => {
    setAnesthesiaStartDate(date);
    setAnesthesiaStartMonth(month);
    setAnesthesiaStartYear(year);
    setIsAnesStartDatePristine(false);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnChangeAnesthesiaStartTime = (hour, min) => {
    setIsAnesthesiaStartTimePristine(false);
    setAnesthesiaStartHour(hour);
    setAnesthesiaStartMinute(min);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnChangeAnesthesiaEndDate = (year, month, date) => {
    setAnesthesiaEndDate(date);
    setAnesthesiaEndMonth(month);
    setAnesthesiaEndYear(year);
    setIsAnesEndDatePristine(false);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnChangeAnesthesiaEndTime = (hour, min) => {
    console.log(`Anesthesia End Time => hour = ${hour} & minute = ${min}`);
    setIsAnesthesiaEndTimePristine(false);
    setAnesthesiaEndHour(hour);
    setAnesthesiaEndMinute(min);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };
  // #endregion

  // #region Face Sheet Tab event handlers
  const handleOnEditPatientFirstName = (e) => {
    const { value } = e.target;
    setPatFirstName(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnEditPatientMiddleName = (e) => {
    const { value } = e.target;
    setPatMiddleName(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnEditPatientLastName = (e) => {
    const { value } = e.target;
    setPatLastName(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnEditPatStreetAddress = (e) => {
    const { value } = e.target;
    setPatStreetAddress(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnEditPatSuite = (e) => {
    const { value } = e.target;
    setPatSuite(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnEditPatCity = (e) => {
    const { value } = e.target;
    setPatCity(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnEditPatState = (e) => {
    const { value } = e.target;
    setPatState(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnEditPatZip = (e) => {
    const { value } = e.target;
    setPatZip(value);
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnClickFaceSheetDownload = () => {
    const formData = new FormData();
    formData.append('formKey', props.faceSheetKey);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/downloadFaceSheet`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          // window.open(res.url, '_blank');
          const a = document.createElement('a');
          a.href = res.url;
          a.download = 'FaceSheet.pdf';
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const uploadPrivateFile = (img, inputRef, formType) => {
    const formData = new FormData();
    if (img) {
      formData.append('file', img);
    } else {
      const img = inputRef.current.files[0];
      formData.append('file', img);
    }
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const options = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    // fetch(`${BASE_URL}/users/uploadPrivateFile`, options)
    fetch(`${BASE_URL}/cases/uploadForm`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          switch (formType) {
            case 'faceSheet':
              props.onUploadFaceSheet(res.fileName);
              setIsUploadingFaceSheet(false);
              break;
            case 'consentForm':
              setConsentFormUploading(false);
              setConsentFormKey(res.fileName);
              break;
            case 'preOpForm':
              setPreOpFormUploading(false);
              setPreOpFormKey(res.fileName);
              break;
            case 'postOpForm':
              setPostOpFormUploading(false);
              setPostOpFormKey(res.fileName);
              break;
            case 'intraOpForm':
              setIntraOpFormUploading(false);
              setIntraOpFormKey(res.fileName);
              break;
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  const createPdf = (images, formType) => {
    const formData = new FormData();
    images.forEach((el, idx) => {
      formData.append('images', dataURItoBlob(el));
    });

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const options = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/createPdf`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          switch (formType) {
            case 'faceSheet':
              props.onUploadFaceSheet(res.fileName);
              setIsUploadingFaceSheet(false);
              break;
            case 'consentForm':
              setConsentFormUploading(false);
              setConsentFormKey(res.fileName);
              break;
            case 'preOpForm':
              setPreOpFormUploading(false);
              setPreOpFormKey(res.fileName);
              break;
            case 'postOpForm':
              setPostOpFormUploading(false);
              setPostOpFormKey(res.fileName);
              break;
            case 'intraOpForm':
              setIntraOpFormUploading(false);
              setIntraOpFormKey(res.fileName);
              break;
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnFaceSheetDrop = (img) => {
    setIsUploadingFaceSheet(true);
    uploadPrivateFile(img, null, 'faceSheet');
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnFaceSheetSelected = () => {
    if (faceSheetFileInputRef.current.files.length > 0) {
      setIsUploadingFaceSheet(true);
      uploadPrivateFile(null, faceSheetFileInputRef, 'faceSheet');
      if (!isCaseDataUpdated) {
        setIsCaseDataUpdated(true);
      }
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnConsentFormDrop = (img) => {
    setConsentFormUploading(true);
    uploadPrivateFile(img, null, 'consentForm');
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnConsentFormCropAccept = () => {
    setConsentFormUploading(true);
    setShowConsentFormCropper(false);
    // croppedConsentFormImages.forEach((el, idx) => { uploadPrivateFile(dataURItoBlob(el), null, 'consentForm'); });
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
    createPdf(consentFormData.croppedImages, 'consentForm');
    // const formData = new FormData();
    // croppedConsentFormImages.forEach((el, idx) => {
    //   formData.append('images', dataURItoBlob(el));
    // });
    // const reqHeaders = new Headers();
    // const token = localStorage.getItem('token');
    // reqHeaders.append('Authorization', `Bearer ${token}`);
    // const options = {
    //   method: 'POST',
    //   body: formData,
    //   redirect: 'follow',
    //   headers: reqHeaders
    // };
    // fetch(`${BASE_URL}/cases/createPdf`, options)
    //   .then(response => response.text())
    //   .then(result => {
    //     const res = JSON.parse(result);
    //     if (res.success) {
    //       setConsentFormUploading(false);
    //       setConsentFormKey(res.fileName);
    //     }
    //   })
    //   .catch(error => console.log('error', error));
  };

  const handleOnPreOpFormCropAccept = () => {
    setPreOpFormUploading(true);
    setShowPreOpCropper(false);
    // croppedPreOpImages.forEach((el, idx) => { uploadPrivateFile(dataURItoBlob(el), null, 'preOpForm'); });
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
    createPdf(preOpData.croppedImages, 'preOpForm');
  };

  const handleOnPostOpFormCropAccept = () => {
    setPostOpFormUploading(true);
    setShowPostOpCropper(false);
    // croppedPostOpImages.forEach((el, idx) => { uploadPrivateFile(dataURItoBlob(el), null, 'postOpForm'); });
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
    createPdf(postOpData.croppedImages, 'postOpForm');
  };

  const handleOnIntraOpFormCropAccept = () => {
    setIntraOpFormUploading(true);
    setShowIntraOpCropper(false);
    // croppedIntraOpImages.forEach((el, idx) => { uploadPrivateFile(dataURItoBlob(el), null, 'intraOpForm'); });
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
    createPdf(intraOpData.croppedImages, 'intraOpForm');
  };

  const handleOnFaceSheetFormCropAccept = () => {
    props.onUploadFaceSheet('');
    setIsUploadingFaceSheet(true);
    setShowFaceSheetCropper(false);
    // croppedFaceSheetImages.forEach((el, idx) => { uploadPrivateFile(dataURItoBlob(el), null, 'faceSheet'); });
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
    createPdf(faceSheetData.croppedImages, 'faceSheet');
  };

  const handleOnConsentFormSelected = () => {
    if (consentFormFileInputRef.current.files.length > 0) {
      setConsentFormUploading(true);
      uploadPrivateFile(null, consentFormFileInputRef, 'consentForm');
      if (!isCaseDataUpdated) {
        setIsCaseDataUpdated(true);
      }
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnPreOpFormDrop = (img) => {
    setPreOpFormUploading(true);
    uploadPrivateFile(img, null, 'preOpForm');
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnPreOpFormSelected = () => {
    if (preOpFormFileInputRef.current.files.length > 0) {
      setPreOpFormUploading(true);
      uploadPrivateFile(null, preOpFormFileInputRef, 'preOpForm');
      if (!isCaseDataUpdated) {
        setIsCaseDataUpdated(true);
      }
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnIntraOpFormDrop = (img) => {
    setIntraOpFormUploading(true);
    uploadPrivateFile(img, null, 'intraOpForm');
    if (!isCaseDataUpdated) {
      setIsCaseDataUpdated(true);
    }
  };

  const handleOnIntraOpFormSelected = () => {
    if (intraOpFormFileInputRef.current.files.length > 0) {
      setIntraOpFormUploading(true);
      uploadPrivateFile(null, intraOpFormFileInputRef, 'intraOpForm');
      if (!isCaseDataUpdated) {
        setIsCaseDataUpdated(true);
      }
    } else {
      console.log('No file is selected!');
    }
  };
  // #endregion

  // Anesthesia procedure search menu items
  const anesthesiaProceduresListSearchItems = [];
  if (anesthesiaProceduresListSearchResults.length > 0) {
    anesthesiaProceduresListSearchResults.forEach(el => {
      anesthesiaProceduresListSearchItems.push(
        <div
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={() => {
            if (anesthesiaProceduresListInputRef.current) {
              anesthesiaProceduresListInputRef.current.value = '';
              anesthesiaProceduresListInputRef.current.focus();
            }
            setAnesthesiaProceduresList((prevState) => {
              return prevState.concat({
                id: el._id,
                text: `${el.shortDescription}`,
                code: el.code,
                description: `${el.shortDescription}`
              });
            });
            setAnesthesiaProceduresListSearchTerm('');
            toggleAnesthesiaProceduresListSearchMenu(false);
          }}
        >
          <div className='ml-2'>
            <div>{el.code}</div>
            <div>{el.shortDescription}</div>
          </div>
        </div>
      );
    });
  }
  // #endregion

  // Anesthesia Procedures on change handler
  const handleOnChangeAnesthesiaProceduresListSearchTerm = (e) => {
    const { value } = e.target;
    setAnesthesiaProceduresListSearchTerm(value);
    if (value.length === 0) {
      toggleAnesthesiaProceduresListSearchMenu(false);
    }
  };

  // #region ------------------- Drawer Handler -------------------
  const handleOnClickSaveCase = () => {
    setSavingCaseForm(true);
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    // Case details
    const dateOfSurgery = new Date(dosYear, dosMonth, dosDate);
    formData.append('dos', dateOfSurgery);
    // Patient details
    formData.append('patientFirstName', patFirstName);
    formData.append('patientMiddleName', patMiddleName);
    formData.append('patientLastName', patLastName);
    const dateOfBirth = new Date(dobYear, dobMonth, dobDate);
    if (!isDobPristine) {
      formData.append('patientDob', dateOfBirth);
    }
    formData.append('patientSex', patGender);
    formData.append('patientStreetAddress', patStreetAddress);
    formData.append('patientSuite', patSuite);
    formData.append('patientCity', patCity);
    formData.append('patientState', patState);
    formData.append('patientZip', patZip);
    formData.append('patientType', patientType);

    formData.append('facility', JSON.stringify(facility));
    formData.append('otherFacility', JSON.stringify(otherFacility));

    formData.append('isOtherSurgeon', otherSurgeonsList.length > 0);
    formData.append('surgeons', JSON.stringify(surgeonsList));
    formData.append('otherSurgeons', JSON.stringify(otherSurgeonsList));

    formData.append('isNoneAnesthesiologist', isNoneAnesthesiologist);
    formData.append('isOtherAnesthesiologist', otherAnesthesiologistsList.length > 0);
    formData.append('anesthesiologists', JSON.stringify(anesthesiologistsList));
    formData.append('otherAnesthesiologists', JSON.stringify(otherAnesthesiologistsList));

    formData.append('isNoneCrna', isNoneCrna);
    formData.append('isOtherCrna', otherCrnasList.length > 0);
    formData.append('otherCrnas', JSON.stringify(otherCrnasList));
    formData.append('crnas', JSON.stringify(crnasList));

    // Procedure details
    formData.append('surgicalProcedures', JSON.stringify(proceduresList));
    formData.append('otherSurgicalProcedures', JSON.stringify(otherProceduresList));
    formData.append('isOtherSurgicalProcedure', otherProceduresList.length > 0);

    const preOpDxArr = [];
    preOpDxList.forEach(el => {
      if (el.code !== 'Other') {
        preOpDxArr.push(pick(['code', 'description'], el));
      }
    });
    formData.append('preOpDx', JSON.stringify(preOpDxArr));
    formData.append('isOtherPreOpDx', isOtherPreOpDx);

    // Anesthesia details
    const anesthesiaProcedures = [];
    anesthesiaProceduresList.forEach(el => {
      if (el.text !== 'Other') {
        anesthesiaProcedures.push(pick(['code', 'description'], el));
      }
    });
    formData.append('anesthesiaProcedures', JSON.stringify(anesthesiaProcedures));
    formData.append('isOtherAnesthesiaProcedure', isOtherAnesthesiaProcedure);

    formData.append('asaScore', asaScore);
    formData.append('isEmergencyAsaScore', isEmergencyAsaScore);

    const anesthesiaTypeArr = [];
    if (isGaEtt) {
      anesthesiaTypeArr.push('GA-ETT');
    }
    if (isGaLma) {
      anesthesiaTypeArr.push('GA-LMA');
    }
    if (isMac) {
      anesthesiaTypeArr.push('MAC');
    }
    if (isSab) {
      anesthesiaTypeArr.push('SAB');
    }
    if (isRegional) {
      anesthesiaTypeArr.push('Regional');
    }
    if (isLocal) {
      anesthesiaTypeArr.push('Local');
    }
    if (isTopical) {
      anesthesiaTypeArr.push('Topical');
    }
    formData.append('anesthesiaTypes', JSON.stringify(anesthesiaTypeArr));

    const anesthesiaModifiersArr = [];
    if (isPronePosition) {
      anesthesiaModifiersArr.push('PronePosition');
    }
    if (isFieldAvoidance) {
      anesthesiaModifiersArr.push('FieldAvoidance');
    }
    if (isControlledHypotension) {
      anesthesiaModifiersArr.push('ControlledHypotension');
    }
    if (isControlledHypothermia) {
      anesthesiaModifiersArr.push('ControlledHypothermia');
    }
    if (isExtremeAge) {
      anesthesiaModifiersArr.push('Extreme Age');
    }
    formData.append('anesthesiaModifiers', JSON.stringify(anesthesiaModifiersArr));

    if (!isAnesStartDatePristine) {
      const anesthesiaStartDateTime = new Date(
        anesthesiaStartYear,
        anesthesiaStartMonth,
        anesthesiaStartDate,
        anesthesiaStartHour,
        anesthesiaStartMinute
      );

      formData.append('anesthesiaStartDate', anesthesiaStartDateTime);
    }

    if (!isAnesEndDatePristine) {
      const anesthesiaEndDateTime = new Date(
        anesthesiaEndYear,
        anesthesiaEndMonth,
        anesthesiaEndDate,
        anesthesiaEndHour,
        anesthesiaEndMinute
      );
      formData.append('anesthesiaEndDate', anesthesiaEndDateTime);
    }

    formData.append('faceSheetKey', props.faceSheetKey);
    // formData.append('faceSheetImageData', faceSheetData);
    formData.append('faceSheetImageData', new Blob([JSON.stringify(faceSheetData)], { type: 'text/plain' }));
    formData.append('consentFormImageData', new Blob([JSON.stringify(consentFormData)], { type: 'text/plain' }));
    formData.append('preOpFormImageData', new Blob([JSON.stringify(preOpData)], { type: 'text/plain' }));
    formData.append('intraOpFormImageData', new Blob([JSON.stringify(intraOpData)], { type: 'text/plain' }));
    formData.append('postOpFormImageData', new Blob([JSON.stringify(postOpData)], { type: 'text/plain' }));

    formData.append('consentFormKey', consentFormKey);
    formData.append('preOpFormKey', preOpFormKey);
    formData.append('postOfFormKey', postOpFormKey);
    formData.append('intraOpFormKey', intraOpFormKey);
    formData.append('notes', notes);

    if (props.editMode) {
      formData.append('id', props.caseData._id);
    }

    const url = !props.editMode ? `${BASE_URL}/cases/saveCase` : `${BASE_URL}/cases/editCase`;

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(url, requestOptions)
      .then(response => response.text())
      .then(result => {
        setSavingCaseForm(false);
        const res = JSON.parse(result);
        if (res.success) {
          if (props.editMode) {
            console.log(res.result);
          }
          props.onClose(false);
          props.onReloadData();
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  const facilityCards = [];
  if (facility && Object.keys(facility).length > 0) {
    facilityCards.push(
      <CaseUserCard
        className='mt-1'
        avatarImageUrl='https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg'
        showCloseIcon
        dataId={facility.id}
        onClose={() => {
          setFacility({});
          if (!isCaseDataUpdated) {
            setIsCaseDataUpdated(true);
          }
        }}
      >
        <div style={{ fontSize: '12px', marginLeft: '8px' }}>
          <div>{facility.facilityName}</div>
          <div>{facility.caseRelation}</div>
        </div>
      </CaseUserCard>
    );
  }

  if (otherFacility && Object.keys(otherFacility).length > 0) {
    facilityCards.push(
      <CaseUserCard
        className='mt-1'
        avatarImageUrl='https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg'
        showCloseIcon
        dataId={otherFacility.id}
        onClose={(id) => {
          setOtherFacility({});
          if (!isCaseDataUpdated) {
            setIsCaseDataUpdated(true);
          }
        }}
      >
        <div style={{ fontSize: '12px', marginLeft: '8px' }}>
          <div>{otherFacility.facilityName}</div>
          <div>{otherFacility.caseRelation}</div>
        </div>
      </CaseUserCard>
    );
  }

  const surgeonCards = [];
  if (surgeonsList.length > 0) {
    surgeonsList.forEach((el) => {
      surgeonCards.push(
        <CaseUserCard
          className='mt-1'
          key={el.user}
          avatarImageUrl={el.icon}
          showCloseIcon
          dataId={el.user}
          onClose={(user) => {
            setSurgeonsList((prevState) => {
              return prevState.filter(el => {
                return el.user !== user;
              });
            });
            // setIsOtherSurgeon(id === '11111');
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  if (otherSurgeonsList.length > 0) {
    otherSurgeonsList.forEach(el => {
      surgeonCards.push(
        <CaseUserCard
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          key={el.id}
          dataId={el.id}
          onClose={(id) => {
            // props.onDeleteOtherSurgeon(id);
            setOtherSurgeonsList((prevState) => {
              return prevState.filter(el => {
                return el.id !== id;
              });
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const anesthesiologistCards = [];
  if (anesthesiologistsList.length > 0) {
    anesthesiologistsList.forEach((el) => {
      anesthesiologistCards.push(
        <CaseUserCard
          className='mt-1'
          avatarImageUrl={el.icon}
          iconClass={el.iconClass}
          showCloseIcon
          key={el.user}
          dataId={el.user}
          onClose={(user) => {
            setAnesthesiologistsList((prevState) => {
              return prevState.filter(el => el.user !== user);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  if (otherAnesthesiologistsList) {
    otherAnesthesiologistsList.forEach(el => {
      anesthesiologistCards.push(
        <CaseUserCard
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          key={el.id}
          dataId={el.id}
          onClose={(id) => {
            // props.onDeleteOtherAnesthesiologist(id);
            setOtherAnesthesiologistsList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const crnaCards = [];
  if (crnasList.length > 0) {
    crnasList.forEach((el) => {
      crnaCards.push(
        <CaseUserCard
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          iconClass={el.iconClass}
          key={el.user}
          dataId={el.user}
          onClose={(user) => {
            setCrnasList((prevState) => {
              return prevState.filter(el => el.user !== user);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  if (otherCrnasList.length > 0) {
    otherCrnasList.forEach(el => {
      crnaCards.push(
        <CaseUserCard
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          key={el.id}
          dataId={el.id}
          onClose={(id) => {
            // props.onDeleteOtherCrna(id);
            setOtherCransList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{el.fullName}</div>
            <div>{el.caseRelation}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const anesthesiaProcedureCards = [];
  if (anesthesiaProceduresList.length > 0) {
    anesthesiaProceduresList.forEach((el) => {
      anesthesiaProcedureCards.push(
        <CaseUserCard
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          noAvatar
          key={el.id}
          dataId={el.id}
          onClose={(id) => {
            setAnesthesiaProceduresList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
            setIsOtherAnesthesiaProcedure(id === '88888');
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{`${el.description} (${el.code})`}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const surgicalProcedureCards = [];
  if (proceduresList.length > 0) {
    proceduresList.forEach((el) => {
      surgicalProcedureCards.push(
        <CaseUserCard
          className='mt-1'
          avatarImageUrl={el.icon}
          showCloseIcon
          noAvatar
          key={el.id}
          dataId={el.id}
          onClose={(id) => {
            setProceduresList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{`${el.description} (${el.code})`}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  if (otherProceduresList.length > 0) {
    otherProceduresList.forEach((el) => {
      surgicalProcedureCards.push(
        <CaseUserCard
          className='mt-1'
          showCloseIcon
          noAvatar
          key={el.id}
          dataId={el.id}
          onClose={(id) => {
            props.onDeleteOtherProcedure(id);
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{`${el.description} (${el.code})`}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  const preOpDxCards = [];
  if (preOpDxList.length > 0) {
    preOpDxList.forEach((el) => {
      preOpDxCards.push(
        <CaseUserCard
          className='mt-1'
          showCloseIcon
          noAvatar
          dataId={el.id}
          onClose={(id) => {
            setPreOpDxList((prevState) => {
              return prevState.filter(el => el.id !== id);
            });
            setIsOtherPreOpDx(id === '77777');
          }}
        >
          <div style={{ fontSize: '12px', marginLeft: '8px' }}>
            <div>{`${el.description} (${el.code})`}</div>
          </div>
        </CaseUserCard>
      );
    });
  }

  // Facesheet View, Edit and Delete menu items handlers
  const handleOnClickViewFaceSheet = () => {
    setShowFaceSheetMenu(false);
    const formData = new FormData();
    formData.append('formKey', props.faceSheetKey);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/downloadFaceSheet`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          setShowFaceSheetMenu(false);
          window.open(res.url, '_blank');
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const handleOnClickEditFaceSheet = () => {
    setFaceSheetData(prvState => ({ ...prvState, activeIndex: 0 }));
    setFaceSheetCropperEditMode(false);
    setShowFaceSheetCropper(true);
    setShowFaceSheetMenu(false);
  };

  // Consent Form View, Edit and Delete menu items handlers
  const handleOnClickViewConsentForm = () => {
    const formData = new FormData();
    formData.append('formKey', consentFormKey);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/downloadForm`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          window.open(res.url, '_blank');
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  // PreOp Form View, Edit and Delete menu items handlers
  const handleOnClickViewPreOpForm = () => {
    const formData = new FormData();
    formData.append('formKey', preOpFormKey);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/downloadForm`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          window.open(res.url, '_blank');
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  // IntraOp Form View, Edit and Delete menu items handlers
  const handleOnClickViewIntraOpForm = () => {
    const formData = new FormData();
    formData.append('formKey', intraOpFormKey);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/downloadForm`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          window.open(res.url, '_blank');
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  // ---------------------------- Tab components ---------------------------
  let activeComp = null;
  switch (activeTab) {
    case 0:
      activeComp = (
        <div className='p-2'>
          {/* Face sheet file card */}
          {props.faceSheetKey !== ''
            ? <div
                className='fx fx__v-center p-2 mt-2 mb-2'
                style={{
                  height: '40px',
                  background: gray[500],
                  borderRadius: '5px',
                  position: 'relative'
                }}
                ref={faceSheetMenuRef}
              >
                {props.faceSheetKey && props.faceSheetKey.endsWith('pdf')
                  ? <i style={{ fontSize: '20px' }} className='fa fa-file-pdf-o' />
                  : <svg width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M17.4984 7.07344L10.9875 0.499219C10.882 0.39375 10.7672 0.304688 10.643 0.229687C10.6102 0.210938 10.5773 0.192188 10.5422 0.175781C10.526 0.166784 10.5084 0.159168 10.4916 0.151872L10.4766 0.145313C10.2656 0.0515625 10.0336 0 9.79688 0H1.71094C0.778125 0 0 0.754687 0 1.6875V22.3125C0 23.2453 0.778125 24 1.71094 24H16.3125C17.2453 24 17.9766 23.2453 17.9766 22.3125V8.26172C17.9766 7.81641 17.8125 7.38984 17.4984 7.07344ZM10.6641 2.5875L15.3703 7.3125H10.6641V2.5875ZM1.6875 22.3125H16.2891V9H10.6875C9.75469 9 8.97656 8.24531 8.97656 7.3125V1.6875H1.6875V22.3125ZM5.20312 16.3242C5.20312 16.8914 5.02734 17.0602 4.68984 17.0602C4.47891 17.0602 4.25859 16.9242 4.10859 16.6266L3.39609 17.1562C3.71016 17.693 4.15312 17.9578 4.83281 17.9578C5.80781 17.9578 6.25781 17.257 6.25781 16.4062V13.5234H5.20312V16.3242ZM7.24219 13.5234H8.74922C9.70781 13.5234 10.493 13.8656 10.493 14.9391C10.493 15.9703 9.69609 16.4297 8.77969 16.4297H8.27344V17.8828H7.24219V13.5234ZM8.73047 15.6094H8.27344V14.3672H8.7C9.20391 14.3672 9.48281 14.5125 9.48281 14.9508C9.48281 15.375 9.23672 15.6094 8.73047 15.6094ZM13.0078 16.2422V15.3984H14.6273V17.4281C14.3391 17.7188 13.793 17.9578 13.1859 17.9578C11.9789 17.9578 11.0414 17.1984 11.0414 15.7289C11.0414 14.2828 12.0141 13.4555 13.1766 13.4555C13.8117 13.4555 14.2664 13.7227 14.5641 14.0227L14.0086 14.6859C13.7977 14.4938 13.5703 14.3484 13.2117 14.3484C12.5695 14.3484 12.1078 14.8477 12.1078 15.6938C12.1078 16.5562 12.4922 17.0578 13.2961 17.0578C13.4531 17.0578 13.6172 17.0156 13.7109 16.9477V16.2422H13.0078Z" fill="#96C8FF" />
                    </svg>
                }
                <div
                  className='fx fx__v-center ml-2'
                  style={{ flexGrow: '1', color: 'white', justifyContent: 'space-between' }}
                >
                  {/* <div>{props.faceSheetKey}</div> */}
                  <div>Face Sheet</div>
                  {isLoadingFaceSheetImageData
                    ? <i className='fa fa-cog fa-spin fa-2x fa-fw' />
                    : <i className='pr-2 fa fa-ellipsis-v' onClick={() => { setShowFaceSheetMenu(!showFaceSheetMenu); }} />
                  }
                  <PopupMenu
                    top={40}
                    right={1}
                    visible={showFaceSheetMenu}
                    parent={faceSheetMenuRef}
                    position="absolute"
                    onOutsideClick={() => { setShowFaceSheetMenu(false); }}
                  >
                    <div className={css(styles.popupMenuContainer)}>
                      {/* View Form */}
                      <div
                        className={`m-item ${css(styles.menuItem)}`}
                        onClick={() => {
                          // handleOnClickViewFaceSheet();
                          setFaceSheetData(prvState => ({ ...prvState, activeIndex: 0 }));
                          setFaceSheetCropperEditMode(true);
                          setShowFaceSheetCropper(true);
                          setShowFaceSheetMenu(false);
                        }}
                      >
                        <div className={css(styles.menuItemIconContainer)}>
                          <i className={`fa fa-eye ${css(styles.menuItemIcon)}`} />
                        </div>
                        <div>View Form</div>
                      </div>
                      {/* Edit Form */}
                      <div
                        className={`m-item ${css(styles.menuItem)}`}
                        onClick={handleOnClickEditFaceSheet}
                      >
                        <div className={css(styles.menuItemIconContainer)}>
                          <i className={`fa fa-pencil ${css(styles.menuItemIcon)}`} />
                        </div>
                        <div>Edit Form</div>
                      </div>
                      {/* Delete Form */}
                      <div
                        className={`m-item ${css(styles.menuItem)}`}
                        onClick={() => {
                          // handleOnClickDeleteFaceSheet
                          props.onDeleteFaceSheet();
                          if (!isCaseDataUpdated) {
                            setIsCaseDataUpdated(true);
                          }
                        }}
                      >
                        <div className={css(styles.menuItemIconContainer)}>
                          <i className={`fa fa-trash ${css(styles.menuItemIcon)}`} />
                        </div>
                        <div>Delete Form</div>
                      </div>
                    </div>
                  </PopupMenu>
                </div>
              </div>
            : <div className='mt-2'>
              {isUploadingFaceSheet &&
                <div
                  style={{
                    height: '40px',
                    background: blue[700],
                    padding: '8px 0',
                    position: 'relative',
                    display: 'flex'
                  }}
                >
                  <Loader
                    iconType="cog"
                    iconSize="2x"
                    text="Uploading Face Sheet"
                    style={{
                      display: 'flex',
                      flexFlow: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  />
                  <i
                    className='fa fa-times-circle'
                    style={{
                      fontSize: '24px',
                      position: 'relative',
                      right: '4px'
                    }}
                  />
                </div>
              }
              {!isUploadingFaceSheet && !isTouchDevice &&
                <FileUploader
                  title='Anesthesia Face Sheet'
                  titleColor={blue[400]}
                  postIcon={bellIcon}
                  text='Drag ‘n Drop OR Select File'
                  showOrLineBreak={false}
                  parentRef={faceSheetFileInputRef}
                  onDrop={handleOnFaceSheetDrop}
                  onChange={handleOnFaceSheetSelected}
                  height='100%'
                  background={gray[600]}
                  highlightBackground='rgba(0, 128, 255, 0.5)'
                  textColor={blue[400]}
                  highlightTextColor={gray[600]}
                  style={{
                    borderRadius: '5px',
                    flexFlow: 'row',
                    justifyContent: 'flex-start',
                    padding: '0 8px'
                  }}
                />
              }
              {!isUploadingFaceSheet && isTouchDevice && !showFaceSheetCamera &&
                <CameraCard
                  text='Face Sheet'
                  onClick={() => setShowFaceSheetCamera(true)}
                />
              }
            </div>
          }
          <div className={`mt-3 ${css(styles.sectionHeading)}`}>
            Patient Details
          </div>
          {/* Patient Gender */}
          <div className='fx mt-1'>
            <div>Gender: </div>
            <div className='ml-4'>
              <RadioButton
                text='Male'
                isSelected={patGender === 'M'}
                onSelectionChanged={() => {
                  setPatGender('M');
                  if (!isCaseDataUpdated) {
                    setIsCaseDataUpdated(true);
                  }
                }}
              />
            </div>
            <div className='ml-4'>
              <RadioButton
                text='Female'
                isSelected={patGender === 'F'}
                onSelectionChanged={() => {
                  setPatGender('F');
                  if (!isCaseDataUpdated) {
                    setIsCaseDataUpdated(true);
                  }
                }}
              />
            </div>
          </div>
          {/* Patient DOB */}
          <div className='fx mt-1'>
            <div>DOB</div>
            <div className='ml-2'>
              <DatePicker
                left={-42}
                right={0}
                date={dobDate}
                month={dobMonth}
                year={dobYear}
                isPrisitine={isDobPristine}
                onChangeDate={(year, month, date) => {
                  setIsDobPristine(false);
                  setDobYear(year);
                  setDobMonth(month);
                  setDobDate(date);
                  if (!isCaseDataUpdated) {
                    setIsCaseDataUpdated(true);
                  }
                }}
              />
            </div>
          </div>
          {/* Patient First, Middle and Last Name */}
          <div className='fx'>
            <div>
              <TextInputWithLabel
                value={patFirstName}
                titleText='First Name'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={112}
                onChange={handleOnEditPatientFirstName}
              />
            </div>
            <div className='ml-2'>
              <TextInputWithLabel
                value={patMiddleName}
                titleText='Middle Name'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={114}
                onChange={handleOnEditPatientMiddleName}
              />
            </div>
            <div className='ml-2'>
              <TextInputWithLabel
                value={patLastName}
                titleText='Last Name'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={114}
                onChange={handleOnEditPatientLastName}
              />
            </div>
          </div>
          {/* Street and Suite */}
          <div className='fx mt-1'>
            <div>
              <TextInputWithLabel
                value={patStreetAddress}
                titleText='Street'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={304}
                onChange={handleOnEditPatStreetAddress}
              />
            </div>
            <div className='ml-2'>
              <TextInputWithLabel
                value={patSuite}
                titleText='Suite'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={45}
                onChange={handleOnEditPatSuite}
              />
            </div>
          </div>
          {/* City, State & Zip */}
          <div className='fx'>
            <div>
              <TextInputWithLabel
                value={patCity}
                titleText='City'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={250}
                onChange={handleOnEditPatCity}
              />
            </div>
            <div className='ml-2'>
              <TextInputWithLabel
                value={patState}
                titleText='State'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={46}
                onChange={handleOnEditPatState}
              />
            </div>
            <div className='ml-2'>
              <TextInputWithLabel
                value={patZip}
                titleText='Zip'
                titleFontSize={12}
                isCompactLayout={true}
                titleColor={gray[200]}
                textColor="white"
                selectionColor="white"
                selectionBackground="#0080FF"
                spellcheck={false}
                width={45}
                onChange={handleOnEditPatZip}
              />
            </div>
          </div>
        </div>
      );
      break;
    case 1:
      activeComp = (
        <div className='p-2'>
          {/* Consent Form FileUploader/Camera */}
          {consentFormKey !== ''
            ? <FileCard
                fileName='Consent Form'
                fileKey={consentFormKey}
                isLoading={isLoadingConsentFormImageData}
                onDelete={() => {
                  // handleOnClickDeleteConsentForm();
                  setConsentFormKey('');
                  if (!isCaseDataUpdated) {
                    setIsCaseDataUpdated(true);
                  }
                }}
                // onView={handleOnClickViewConsentForm}
                onView={() => {
                  setConsentFormCropperEditMode(true);
                  setShowConsentFormCropper(true);
                }}
                onEdit={() => {
                  setConsentFormCropperEditMode(false);
                  setShowConsentFormCropper(true);
                }}
              />
            : <div className='mt-4'>
              {isConsentFormUploading &&
                <div
                  style={{
                    height: '40px',
                    background: blue[700],
                    padding: '8px 0',
                    position: 'relative',
                    display: 'flex'
                  }}>
                  <Loader
                    iconType="cog"
                    iconSize="2x"
                    text="Uploading Consent Form"
                    style={{
                      display: 'flex',
                      flexFlow: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  />
                  <i
                    className='fa fa-times-circle'
                    style={{
                      fontSize: '24px',
                      position: 'relative',
                      right: '4px'
                    }}
                  />
                </div>
              }
              {!isConsentFormUploading && !isTouchDevice &&
                <FileUploader
                  title='Anesthesia Consent Form'
                  titleColor={blue[400]}
                  postIcon={bellIcon}
                  text='Drag ‘n Drop OR Select File'
                  showOrLineBreak={false}
                  parentRef={consentFormFileInputRef}
                  onDrop={handleOnConsentFormDrop}
                  onChange={handleOnConsentFormSelected}
                  height='100%'
                  background={gray[600]}
                  highlightBackground='rgba(0, 128, 255, 0.5)'
                  textColor={blue[400]}
                  highlightTextColor={gray[600]}
                  style={{
                    borderRadius: '4px',
                    flexFlow: 'row',
                    justifyContent: 'flex-start',
                    padding: '0 8px'
                  }}
                />
              }
              {!isConsentFormUploading && isTouchDevice && !showConsentFormCamera &&
                <CameraCard
                  text='Consent Form'
                  onClick={() => setShowConsentFormCamera(true)}
                />
              }
            </div>
          }
          {/* PreOp Anesthesia Form */}
          {preOpFormKey !== ''
            ? <FileCard
              fileName='Pre/Post Op Forms'
              fileKey={preOpFormKey}
              isLoading={isLoadingPreOpImageData}
              onDelete={() => {
                // handleOnClickDeletePreOpForm();
                setPreOpFormKey('');
                if (!isCaseDataUpdated) {
                  setIsCaseDataUpdated(true);
                }
              }}
              onView={() => {
                // handleOnClickViewPreOpForm();
                setPreOpCropeprEditMode(true);
                setShowPreOpCropper(true);
              }}
              onEdit={() => {
                setPreOpCropeprEditMode(false);
                setShowPreOpCropper(true);
              }}
            />
            : <div className='mt-4'>
              {isPreOpFormUploading &&
                <div
                  style={{
                    height: '40px',
                    background: blue[700],
                    padding: '8px 0',
                    position: 'relative',
                    display: 'flex'
                  }}
                >
                  <Loader
                    iconType="cog"
                    iconSize="2x"
                    text="Uploading PreOp Form"
                    style={{
                      display: 'flex',
                      flexFlow: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  />
                  <i
                    className='fa fa-times-circle'
                    style={{
                      fontSize: '24px',
                      position: 'relative',
                      right: '4px'
                    }}
                  />
                </div>
              }
              {!isPreOpFormUploading && !isTouchDevice &&
                <FileUploader
                  title='Anesthesia PreOp Form'
                  titleColor={blue[400]}
                  postIcon={bellIcon}
                  text='Drag ‘n Drop OR Select File'
                  showOrLineBreak={false}
                  parentRef={preOpFormFileInputRef}
                  onDrop={handleOnPreOpFormDrop}
                  onChange={handleOnPreOpFormSelected}
                  height='100%'
                  background={gray[600]}
                  highlightBackground='rgba(0, 128, 255, 0.5)'
                  textColor={blue[400]}
                  highlightTextColor={gray[600]}
                  style={{
                    borderRadius: '4px',
                    flexFlow: 'row',
                    justifyContent: 'flex-start',
                    padding: '0 8px'
                  }}
                />
              }
              {!isPreOpFormUploading && isTouchDevice && !showPreOpFormCamera &&
                <CameraCard
                  text="PreOp Form"
                  onClick={() => setShowPreOpFormCamera(true)}
                />
              }
            </div>
          }
          {/* IntraOp Anesthesia Form */}
          {intraOpFormKey !== ''
            ? <FileCard
              fileName='Intra Op Form'
              fileKey={intraOpFormKey}
              isLoading={isLoadingIntraOpImageData}
              onDownload={handleOnClickFaceSheetDownload}
              onDelete={() => {
                setIntraOpFormKey('');
                if (!isCaseDataUpdated) {
                  setIsCaseDataUpdated(true);
                }
              }}
              onView={() => {
                // handleOnClickViewIntraOpForm();
                setIntraOpCropeprEditMode(true);
                setShowIntraOpCropper(true);
              }}
              onEdit={() => {
                setIntraOpCropeprEditMode(false);
                setShowIntraOpCropper(true);
              }}
            />
            : <div className='mt-4'>
              {isIntraOpFormUploading &&
                <div
                  style={{
                    height: '40px',
                    background: blue[700],
                    padding: '8px 0',
                    position: 'relative',
                    display: 'flex'
                  }}
                >
                  <Loader
                    iconType="cog"
                    iconSize="2x"
                    text="Uploading Consent Form"
                    style={{
                      display: 'flex',
                      flexFlow: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  />
                  <i
                    className='fa fa-times-circle'
                    style={{
                      fontSize: '24px',
                      position: 'relative',
                      right: '4px'
                    }}
                  />
                </div>
              }
              {!isIntraOpFormUploading && !isTouchDevice &&
                <FileUploader
                  title='Anesthesia IntraOp Form'
                  titleColor={blue[400]}
                  postIcon={bellIcon}
                  text='Drag ‘n Drop OR Select File'
                  showOrLineBreak={false}
                  parentRef={intraOpFormFileInputRef}
                  onDrop={handleOnIntraOpFormDrop}
                  onChange={handleOnIntraOpFormSelected}
                  height='100%'
                  background={gray[600]}
                  highlightBackground='rgba(0, 128, 255, 0.5)'
                  textColor={blue[400]}
                  highlightTextColor={gray[600]}
                  style={{
                    borderRadius: '4px',
                    flexFlow: 'row',
                    justifyContent: 'flex-start',
                    padding: '0 8px'
                  }}
                />
              }
              {!isIntraOpFormUploading && isTouchDevice && !showIntraOpFormCamera &&
                <CameraCard
                  text='IntraOp From'
                  onClick={() => setShowIntraOpFormCamera(true)}
                />
              }
            </div>
          }
        </div>
      );
      break;
    case 3:
      activeComp = (
        <div
          className='mt-2 pl-2 pr-2'
          style={{ height: 'calc(100% - 16px)' }}
        >
          <textarea
            className={css(styles.notes)}
            style={{ fontSize: '1rem' }}
            value={notes}
            onChange={handleOnEditNotes}
            placeholder='Type note...'
          />
        </div>
      );
      break;
    case 2:
      activeComp = (
        <div className='p-2'>
          {/* Facility MaskedDataInput */}
          <MaskedDataInput
            visible={isFacilityListSearchMenuOpen}
            apiSearchPath='users/searchFacilities'
            title='Search Facilities'
            onClose={() => toggleFacilityListSearchMenu(false)}
            apiDataKey='facilities'
            noneObject={{
              _id: '11111Fac',
              npi: 0,
              avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg',
              facilityName: ''
            }}
            propVar='facilityName'
            menuItemTemplate={(el, highlight, noneObj = false) => {
              return (
                <div
                  className={`fx fx__v-center ${css(styles.menuItem)}`}
                  onClick={() => {
                    if (el.npi === 0) {
                      setOtherFacility(el);
                    } else {
                      setFacility({
                        facility: el._id,
                        facilityName: el.facilityName,
                        npi: el.npi
                      });
                    }
                    toggleFacilityListSearchMenu(false);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                >
                  {/* Avatar */}
                  <div className={css(styles.menuItemAvatarContainer)}>
                    <img
                      src={el.avatarImageUrl}
                      alt='Facility Avatar'
                      className={css(styles.menuItemAvatarImg)}
                    />
                  </div>
                  {/* Facility Name */}
                  <div className='ml-2'>
                    <div>{el.facilityName}</div>
                    <div>{el.taxonomy}</div>
                  </div>
                </div>
              );
            }}
          />
          {/* Surgeons MaskedDataInput */}
          <MaskedDataInput
            visible={isSurgeonsListSearchMenuOpen}
            apiSearchPath='users/searchMedProsSurgeons'
            title='Search Surgeon(s)'
            onClose={() => toggleSurgeonsListSearchMenu(false)}
            apiDataKey='users'
            noneObject={{
              _id: '22222Surg',
              lastName: '',
              firstName: '',
              icon: '',
              avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
              npi: 0
            }}
            propVar='lastName'
            menuItemTemplate={(el, highlight, noneObj = false) => {
              return (
                <div
                  key={el._id}
                  className={`fx fx__v-center ${css(styles.menuItem)}`}
                  onClick={() => {
                    if (el.npi === 0) {
                      setOtherSurgeonsList((prvState) => {
                        return prvState.concat({
                          id: el._id,
                          text: `${el.lastName}, ${el.firstName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: 0,
                          caseRelation: 'Surgeon'
                        });
                      });
                    } else {
                      setSurgeonsList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.lastName}, ${el.firstName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: el.npi,
                          caseRelation: 'Surgeon'
                        });
                      });
                    }
                    toggleSurgeonsListSearchMenu(false);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                >
                  {/* Avatar */}
                  <div className={css(styles.menuItemAvatarContainer)}>
                    <img
                      src={el.avatarImageUrl}
                      alt='Surgeon Avatar'
                      className={css(styles.menuItemAvatarImg)}
                    />
                  </div>
                  {/* Surgeon Name && Specialty */}
                  <div className='ml-2'>
                    {noneObj
                      ? <div>{el.lastName}</div>
                      : <div>{`${el.lastName}, ${el.firstName}`}</div>
                    }
                    <div>{el.specialty}</div>
                  </div>
                </div>
              );
            }}
          />
          {/* Anesthesiologists MaskedDataInput */}
          <MaskedDataInput
            visible={isAnesthesiologistsListSearchMenuOpen}
            apiSearchPath='users/searchMedProsAnesthesiologists'
            title='Search Anesthesiologist(s)'
            onClose={() => toggleAnesthesiologistsListSearchMenu(false)}
            apiDataKey='users'
            noneObject={{
              _id: '22222Surg',
              lastName: '',
              firstName: '',
              icon: '',
              avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
              npi: 0
            }}
            propVar='lastName'
            menuItemTemplate={(el, highlight, noneObj = false) => {
              return (
                <div
                  key={el._id}
                  className={`fx fx__v-center ${css(styles.menuItem)}`}
                  onClick={() => {
                    if (el.npi === 0) {
                      setOtherAnesthesiologistsList((prvState) => {
                        return prvState.concat({
                          id: el._id,
                          text: `${el.displayName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: 0,
                          caseRelation: 'Anesthesiologist'
                        });
                      });
                    } else {
                      setAnesthesiologistsList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.displayName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: el.npi,
                          caseRelation: 'Anesthesiologist'
                        });
                      });
                    }
                    toggleAnesthesiologistsListSearchMenu(false);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                >
                  {/* Avatar */}
                  <div className={css(styles.menuItemAvatarContainer)}>
                    <img
                      src={el.avatarImageUrl}
                      alt='Anesthesiologist Avatar'
                      className={css(styles.menuItemAvatarImg)}
                    />
                  </div>
                  {/* Anesthesiologist Name && Specialty */}
                  <div className='ml-2'>
                    {noneObj
                      ? <div>{el.lastName}</div>
                      : <div>{`${el.lastName}, ${el.firstName}`}</div>
                    }
                    <div>{el.specialty}</div>
                  </div>
                </div>
              );
            }}
          />
          {/* CRNA(s) MaskedDataInput */}
          <MaskedDataInput
            visible={isCrnasListSearchMenuOpen}
            apiSearchPath='users/searchMedProsCrnas'
            title='Search CRNA(s)/ CAA(s)'
            onClose={() => toggleCrnasListSearchMenu(false)}
            apiDataKey='users'
            noneObject={{
              _id: '22222Surg',
              lastName: '',
              firstName: '',
              icon: '',
              avatarImageUrl: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
              npi: 0
            }}
            propVar='lastName'
            menuItemTemplate={(el, highlight, noneObj = false) => {
              return (
                <div
                  key={el._id}
                  className={`fx fx__v-center ${css(styles.menuItem)}`}
                  onClick={() => {
                    if (el.npi === 0) {
                      setOtherCransList((prvState) => {
                        return prvState.concat({
                          id: el._id,
                          text: `${el.displayName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: 0,
                          caseRelation: 'CRNA'
                        });
                      });
                    } else {
                      setCrnasList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.displayName}`,
                          icon: el.avatarImageUrl,
                          user: el._id,
                          fullName: `${el.lastName}, ${el.firstName}`,
                          npi: el.npi,
                          caseRelation: 'CRNA'
                        });
                      });
                    }
                    toggleCrnasListSearchMenu(false);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                >
                  {/* Avatar */}
                  <div className={css(styles.menuItemAvatarContainer)}>
                    <img
                      src={el.avatarImageUrl}
                      alt='Anesthesiologist Avatar'
                      className={css(styles.menuItemAvatarImg)}
                    />
                  </div>
                  {/* CRNA Name & Specialty */}
                  <div className='ml-2'>
                    {noneObj
                      ? <div>{el.lastName}</div>
                      : <div>{`${el.lastName}, ${el.firstName}`}</div>
                    }
                    <div>{el.specialty}</div>
                  </div>
                </div>
              );
            }}
          />
          {/* Procedures MaskedDataInput */}
          <MaskedDataInput
            visible={isProceduresListSearchMenuOpen}
            apiSearchPath='cpt/searchCpt2'
            title='Search Procedures'
            onClose={() => toggleProceduresListSearchMenu(false)}
            apiDataKey='cptCodes'
            noneObject={{
              _id: '33333Procedure',
              code: 'N/A',
              description: '',
              longDescription: '',
              shortDescription: '',
              npi: 0
            }}
            propVar='shortDescription'
            menuItemTemplate={(el, highlight, noneObj = false) => {
              return (
                <div
                  key={el._id}
                  className={`fx fx__v-center ${css(styles.menuItem)}`}
                  onClick={() => {
                    if (el.npi > -1) {
                      setOtherProceduresList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.shortDescription}`,
                          code: el.code,
                          description: `${el.shortDescription}`,
                          npi: el.npi
                        });
                      });
                    } else {
                      setProceduresList((prevState) => {
                        return prevState.concat({
                          id: el._id,
                          text: `${el.shortDescription}`,
                          code: el.code,
                          description: `${el.shortDescription}`
                        });
                      });
                    }
                    toggleProceduresListSearchMenu(false);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                >
                  <div className='ml-2'>
                    {!noneObj
                      ? <>
                        <div style={{ fontWeight: 'bold' }}>
                          CPT CODE: {el.code}
                        </div>
                        <div>{getHighlightedText(el.longDescription, highlight)}</div>
                        </>
                      : <div>{el.shortDescription}</div>
                    }
                    {/* <div style={{ fontWeight: '700' }}>
                      CPT CODE: {el.code}
                    </div>
                    <div>{getHighlightedText(el.longDescription, highlight)}</div> */}
                  </div>
                </div>
              );
            }}
          />
          {/* PreOp Diagnosis MaskedDataInput */}
          <MaskedDataInput
            visible={isDiagnosisSearchMenuOpen}
            apiSearchPath='icd10/searchIcd10'
            title='Search Diagnosis Code'
            onClose={() => toggleDiagnosisSearchMenu(false)}
            apiDataKey='icd10Codes'
            menuItemTemplate={(el) => {
              return (
                <div
                  className={`fx fx__v-center ${css(styles.menuItem)}`}
                  onClick={() => {
                    setPreOpDxList((prevState) => {
                      return prevState.concat({
                        id: el._id,
                        code: el.fullCode,
                        description: el.abbreviatedDescription
                      });
                    });
                    toggleDiagnosisSearchMenu(false);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                >
                  <div className='ml-2'>
                    <div>{el.fullCode}</div>
                    <div>{el.abbreviatedDescription}</div>
                  </div>
                </div>
              );
            }}
          />
          {/* Facility Details */}
          <div className='mt-2'>
            <div className={css(styles.sectionHeading)}>
              Facility Details
            </div>
            {/* Facility Name */}
            <div>
              <div
                ref={facilityRef}
                className={css(styles.formFieldContainer)}
                style={{ position: 'relative' }}
              >
                <CaseField
                  heading='Facility'
                  showPlusIcon={facilityCards.length === 0}
                  showIsRequired={facilityCards.length === 0}
                  className='mt-4'
                  handleOnClickPlusIcon={() => {
                    toggleFacilityListSearchMenu(true);
                  }}
                />
                {facilityCards.length > 0 && <div className='mt-1'>{facilityCards}</div>}
              </div>
            </div>
          </div>
          {/* Provider Details */}
          <div className='mt-4'>
            <div className={css(styles.sectionHeading)}>
              Provider Details
            </div>
            {/* Surgeons List */}
            <div
              className={`mt-2 ${css(styles.formFieldContainer)}`}
              ref={surgeonsRef}
            >
              <CaseField
                heading='Surgeons'
                showPlusIcon
                showIsRequired={surgeonsList.length === 0 && surgeonCards.length === 0}
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  toggleSurgeonsListSearchMenu(true);
                }}
              />
              {surgeonCards.length > 0 && <div className='mt-1'>{surgeonCards}</div>}
            </div>
            {/* Anesthesiologists List */}
            <div
              className={`mt-4 ${css(styles.formFieldContainer)}`}
              ref={anesthesiologistsRef}
            >
              <CaseField
                heading='Anesthesiologist(s)'
                showPlusIcon
                showIsRequired={!isNoneAnesthesiologist && anesthesiologistCards.length === 0}
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  toggleAnesthesiologistsListSearchMenu(true);
                }}
              />
              {anesthesiologistCards.length > 0 &&
                <div className='mt-1'>
                  {anesthesiologistCards}
                </div>
              }
            </div>
            {/* CRNAs List */}
            <div
              className={`mt-4 ${css(styles.formFieldContainer)}`}
              ref={crnasRef}
              style={{ position: 'relative' }}
            >
              <CaseField
                heading='CRNA(s)/ CAA(s)'
                showPlusIcon
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  toggleCrnasListSearchMenu(true);
                }}
              />
              {crnaCards.length > 0 && <div className='mt-1'>{crnaCards}</div>}
            </div>
          </div>
          {/* Procedure details */}
          <div className='mt-4'>
            <div className={css(styles.sectionHeading)}>
              Procedure Details
            </div>
            {/* Surgical Procedure(s) List */}
            <div
              className={`mt-2 ${css(styles.formFieldContainer)}`}
              ref={proceduresRef}
            >
              <CaseField
                heading='Procedure(s)'
                showPlusIcon
                showIsRequired={proceduresList.length === 0 && surgicalProcedureCards.length === 0}
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  toggleProceduresListSearchMenu(true);
                }}
              />
              {surgicalProcedureCards.length > 0 &&
                <div className='mt-1'>
                  {surgicalProcedureCards}
                </div>
              }
              {/* {proceduresList.length < 1 &&
                <span className={css(styles.requiredHeadingMsg)}>
                  Must add atleast one procedure
                </span>
              } */}
            </div>
            {/* PreOpDx */}
            <div
              className={`mt-4 ${css(styles.formFieldContainer)}`}
              ref={diagnosisRef}
            >
              <CaseField
                heading='Diagnosis'
                showPlusIcon
                showIsRequired={preOpDxList.length === 0}
                className='mt-4'
                handleOnClickPlusIcon={() => {
                  toggleDiagnosisSearchMenu(true);
                }}
              />
              {preOpDxList.length > 0 && <div className='mt-1'>{preOpDxCards}</div>}
            </div>
          </div>
          {/* Anesthesia details */}
          <div className='mt-4'>
            <div className={css(styles.sectionHeading)}>
              Anesthesia Details
            </div>
            {/* Anesthesia Services */}
            <div
              className={`mt-2 ${css(styles.formFieldContainer)}`}
              ref={anesthesiaProceduresRef}
              style={{ position: 'relative' }}
            >
              <TextInput
                placeholder='Anesthesia Service(s)'
                value={anesthesiaProceduresListSearchTerm}
                onChange={handleOnChangeAnesthesiaProceduresListSearchTerm}
                style={{
                  width: '359px',
                  height: '20px',
                  background: gray[800],
                  border: '1px solid #60A0E0',
                  borderWidth: '0 0 1px 0',
                  marginBottom: '2px',
                  fontSize: '14px',
                  paddingBottom: '8px'
                }}
              />
              {anesthesiaProceduresList.length < 1 &&
                <span className={css(styles.requiredHeadingMsg)}>
                  Must add atleast one procedure
                </span>
              }
              {anesthesiaProceduresList.length > 0 && <div className='mt-1'>{anesthesiaProcedureCards}</div>}
              <PopupMenu
                top={22}
                left={1}
                zIndex='1550'
                position="absolute"
                visible={isAnesthesiaProceduresListSearchMenuOpen}
                parent={anesthesiaProceduresRef}
                onOutsideClick={() => { toggleAnesthesiaProceduresListSearchMenu(false); }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexFlow: 'column',
                    width: '359px',
                    background: gray[700],
                    maxHeight: '200px'
                  }}
                >
                  <div style={{ height: 'calc(100% - 41px)', overflowY: 'auto' }}>
                    {anesthesiaProceduresListSearchItems}
                  </div>
                  <div className={css(styles.pinnedMenuItemsContainer)}>
                    {/* Other */}
                    <div
                      className={`fx fx__v-center ${css(styles.menuItem)}`}
                      style={{ height: '40px' }}
                      onClick={() => {
                        if (anesthesiaProceduresListInputRef.current) {
                          anesthesiaProceduresListInputRef.current.value = '';
                          anesthesiaProceduresListInputRef.current.focus();
                        }
                        setAnesthesiaProceduresList((prevState) => {
                          return prevState.concat({
                            id: '88888',
                            text: 'Other',
                            code: '88888',
                            description: 'Other'
                          });
                        });
                        setIsOtherAnesthesiaProcedure(true);
                        setAnesthesiaProceduresListSearchTerm('');
                        toggleAnesthesiaProceduresListSearchMenu(false);
                      }}
                    >
                      <div className='ml-2'>Other</div>
                    </div>
                  </div>
                </div>
              </PopupMenu>
            </div>
            {/* ASA Score */}
            <div className='mt-4'>
              <div className={css(styles.sectionSubHeading)}>
                ASA Score
                {asaScore.length < 1
                  ? <span className={`ml-2 ${css(styles.requiredHeadingMsg)}`}>Required</span>
                  : null
                }
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={40}
                  height={32}
                  className='mr-2'
                  onClick={() => {
                    setAsaScore('1');
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={asaScore === '1'}
                >
                  1
                </PressedButton>
                <PressedButton
                  width={40}
                  height={32}
                  className='mr-2'
                  onClick={() => {
                    setAsaScore('2');
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={asaScore === '2'}
                >
                  2
                </PressedButton>
                <PressedButton
                  width={50}
                  height={32}
                  className='mr-2'
                  onClick={() => {
                    setAsaScore('3');
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={asaScore === '3'}
                >
                  3 (+1)
                </PressedButton>
                <PressedButton
                  width={50}
                  height={32}
                  className='mr-2'
                  onClick={() => {
                    setAsaScore('4');
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={asaScore === '4'}
                >
                  4 (+2)
                </PressedButton>
                <PressedButton
                  width={50}
                  height={32}
                  className='mr-2'
                  onClick={() => {
                    setAsaScore('5');
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={asaScore === '5'}
                >
                  5 (+3)
                </PressedButton>
                <PressedButton
                  width={40}
                  height={32}
                  className='mr-2'
                  onClick={() => {
                    setAsaScore('6');
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={asaScore === '6'}
                >
                  6
                </PressedButton>
                <PressedButton
                  width={32}
                  height={32}
                  className='ml-2'
                  style={{ borderRadius: '50%' }}
                  onClick={() => {
                    setIsEmergencyAsaScore(!isEmergencyAsaScore);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isEmergencyAsaScore}
                >
                  E
                </PressedButton>
              </div>
            </div>
            {/* Anesthesia Type */}
            <div className='mt-4'>
              <div className={css(styles.sectionSubHeading)}>
                Anesthesia Type
                {!isGaEtt && !isGaLma && !isMac && !isEpidural && !isSab && !isRegional && !isLocal && !isTopical
                  ? <span className={`ml-2 ${css(styles.requiredHeadingMsg)}`}>Required</span>
                  : null
                }
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={78}
                  onClick={() => {
                    setisGaEtt(!isGaEtt);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isGaEtt}
                >
                  GA-ETT
                </PressedButton>
                <PressedButton
                  width={78}
                  className='ml-2'
                  onClick={() => {
                    setisGaLma(!isGaLma);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isGaLma}
                >
                  GA-LMA
                </PressedButton>
                <PressedButton
                  width={78}
                  className='ml-2'
                  onClick={() => {
                    setisMac(!isMac);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isMac}
                >
                  MAC
                </PressedButton>
                <PressedButton
                  width={78}
                  className='ml-2'
                  onClick={() => {
                    setisEpidural(!isEpidural);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isEpidural}
                >
                  Epidural
                </PressedButton>
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={78}
                  onClick={() => {
                    setisSab(!isSab);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isSab}
                >
                  SAB
                </PressedButton>
                <PressedButton
                  width={78}
                  className='ml-2'
                  onClick={() => {
                    setisRegional(!isRegional);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isRegional}
                >
                  Regional
                </PressedButton>
                <PressedButton
                  width={78}
                  className='ml-2'
                  onClick={() => {
                    setisLocal(!isLocal);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isLocal}
                >
                  Local
                </PressedButton>
                <PressedButton
                  width={78}
                  className='ml-2'
                  onClick={() => {
                    setisTopical(!isTopical);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isTopical}
                >
                  Topical
                </PressedButton>
              </div>
            </div>
            {/* Anesthesia Modifiers */}
            <div className='mt-4'>
              <div className={css(styles.sectionSubHeading)}>
                Anesthesia Modifiers (Leave blank if none)
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={165}
                  className={css(styles.anesModBtns)}
                  onClick={() => {
                    setIsPronePosition(!isPronePosition);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isPronePosition}
                >
                  Prone Position
                </PressedButton>
                <PressedButton
                  width={165}
                  className={`ml-2 ${css(styles.anesModBtns)}`}
                  onClick={() => {
                    setIsFieldAvoidance(!isFieldAvoidance);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isFieldAvoidance}
                >
                  Field Avoidance
                </PressedButton>
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={165}
                  className={css(styles.anesModBtns)}
                  onClick={() => {
                    setIsControlledHypotension(!isControlledHypotension);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isControlledHypotension}
                >
                  <div style={{ fontSize: '12px', textAlign: 'center', lineHeight: '12px' }}>
                    <div>Controlled Hypotension</div>
                    <div>99135</div>
                  </div>
                </PressedButton>
                <PressedButton
                  width={165}
                  className={`ml-2 ${css(styles.anesModBtns)}`}
                  onClick={() => {
                    setIsControlledHypothermia(!isControlledHypothermia);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isControlledHypothermia}
                >
                  <div style={{ fontSize: '12px', textAlign: 'center', lineHeight: '12px' }}>
                    <div>Controlled Hypothermia</div>
                    <div>99116</div>
                  </div>
                </PressedButton>
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={165}
                  className={css(styles.anesModBtns)}
                  onClick={() => {
                    setIsExtremeAge(!isExtremeAge);
                    if (!isCaseDataUpdated) {
                      setIsCaseDataUpdated(true);
                    }
                  }}
                  isPressed={isExtremeAge}
                >
                  <div style={{ fontSize: '12px', textAlign: 'center', lineHeight: '12px' }}>
                    <div>Extreme Age (&lt;1y OR &gt; 70)</div>
                    <div>99100</div>
                  </div>
                </PressedButton>
              </div>
            </div>
          </div>
          {/* Times */}
          <div className='mt-4' style={{ position: 'relative' }}>
            <div className='fx' style={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <div className={css(styles.sectionHeading)}>
                Times
              </div>
            </div>
            {/* Anesthesia Date Time */}
            <div className='fx mt-2' style={{ justifyContent: 'space-between', fontSize: '14px' }}>
              {/* Anesthesia Start */}
              <div>
                <div>Anesthesia Start</div>
                <div className='fx'>
                  <DatePicker
                    date={anesthesiaStartDate}
                    month={anesthesiaStartMonth}
                    year={anesthesiaStartYear}
                    onChangeDate={handleOnChangeAnesthesiaStartDate}
                    isPrisitine={isAnesStartDatePristine}
                  />
                  <TimePicker
                    className='ml-1'
                    hour={anesthesiaStartHour}
                    minute={anesthesiaStartMinute}
                    timeFormat='24H'
                    timePeriod='AM'
                    isPristine={isAnesthesiaStartTimePristine}
                    onChangeTime={handleOnChangeAnesthesiaStartTime}
                    disableTimeFormat
                  />
                </div>
              </div>

              {/* Anesthesia End */}
              <div>
                <div>Anesthesia End</div>
                <div className='fx'>
                  <DatePicker
                    left={0}
                    right={-130}
                    date={anesthesiaEndDate}
                    month={anesthesiaEndMonth}
                    year={anesthesiaEndYear}
                    onChangeDate={handleOnChangeAnesthesiaEndDate}
                    isPrisitine={isAnesEndDatePristine}
                  />
                  <TimePicker
                    className='ml-1'
                    left={null}
                    right={1}
                    hour={anesthesiaEndHour}
                    minute={anesthesiaEndMinute}
                    timeFormat='24H'
                    timePeriod='AM'
                    isPristine={isAnesthesiaEndTimePristine}
                    onChangeTime={handleOnChangeAnesthesiaEndTime}
                    disableTimeFormat
                  />
                </div>
              </div>
              {/* Total Anesthesia Time */}
              <div>
                <div>Anesthesia Time</div>
                <div>
                  {!isAnesthesiaStartTimePristine && !isAnesthesiaEndTimePristine ? `${anesthesiaTime} hours` : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
      break;
  }

  let surgeonsNames = '';
  if (surgeonsList.length > 0) {
    for (let i = 0; i < surgeonsList.length; i++) {
      if (i === surgeonsList.length - 1) {
        surgeonsNames += surgeonsList[i].fullName;
      } else {
        surgeonsNames += `${surgeonsList[i].fullName} | `;
      }
    }
  }

  let procedureNames = '';
  for (let i = 0; i < proceduresList.length; i++) {
    if (i === proceduresList.length - 1) {
      procedureNames += proceduresList[i].description.toLowerCase();
    } else {
      procedureNames += `${proceduresList[i].description.toLowerCase()} | `;
    }
  }

  // Case Header
  let facilityName = '';
  if (Object.keys(facility).length > 0) {
    facilityName = facility.facilityName;
  } else if (Object.keys(otherFacility).length > 0) {
    facilityName = otherFacility.facilityName;
  }

  let patientSummary = '';
  const patientAge = (new Date().getTime() - new Date(dobYear, dobMonth, dobDate).getTime()) / (365 * 24 * 60 * 60 * 1000);
  if (patLastName !== '') {
    patientSummary += patLastName;
  }
  if (patFirstName !== '') {
    patientSummary += `, ${patFirstName}`;
  }
  if (dobYear && dobMonth && dobDate) {
    patientSummary += `(${Math.round(patientAge)}y${patGender}, ${new Date(dobYear, dobMonth, dobDate).toLocaleDateString()})`;
  }

  let drawerWidth = window.innerWidth <= 700 ? window.innerWidth : 375;
  if (showFaceSheetCamera ||
    showConsentFormCamera ||
    showPreOpFormCamera ||
    showPostOpFormCamera ||
    showIntraOpFormCamera ||
    showFaceSheetCropper ||
    showConsentFormCropper ||
    showPreOpCropper ||
    showPostOpCropper ||
    showIntraOpCropper
  ) {
    drawerWidth = window.innerWidth;
  }

  return (
    <Drawer
      placement="right"
      visible={true}
      toggleDrawer={() => {
        props.onClose(isCaseDataUpdated);
      }}
      zIndex="2000"
      width={drawerWidth}
    >
      {!isConsentFormUploading && isTouchDevice && showConsentFormCamera &&
        <CameraContainer
          images={consentFormData.images}
          onSkip={() => { setShowConsentFormCamera(false); }}
          onCancel={() => {
            setShowConsentFormCropper(false);
            setShowConsentFormCamera(false);
          }}
          onEdit={() => {
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(true);
          }}
          onScan={(img) => {
            if (consentFormData.isReScan) {
              setConsentFormData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  cropperImages: tmpCroppedImages
                };
              });
            } else {
              setConsentFormData((prvState) => ({
                ...prvState,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(true);
          }}
        />
      }
      {!isPreOpFormUploading && isTouchDevice && showPreOpFormCamera &&
        <CameraContainer
          images={preOpData.images}
          onSkip={() => { setShowPreOpFormCamera(false); }}
          onEdit={() => {
            setShowPreOpFormCamera(false);
            setShowPreOpCropper(true);
          }}
          onCancel={() => {
            setShowPreOpCropper(false);
            setShowPreOpFormCamera(false);
          }}
          onScan={(img) => {
            if (preOpData.isReScan) {
              setPreOpData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  cropperImages: tmpCroppedImages
                };
              });
            } else {
              setPreOpData((prvState) => ({
                ...prvState,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowPreOpFormCamera(false);
            setShowPreOpCropper(true);
          }}
        />
      }
      {!isPostOpFormUploading && isTouchDevice && showPostOpFormCamera &&
        <CameraContainer
          onSkip={() => { setShowPostOpFormCamera(false); }}
          onScan={(img) => {
            if (postOpData.isReScan) {
              setPostOpData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages
                };
              });
            } else {
              setPostOpData((prvState) => ({
                ...prvState,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowPostOpFormCamera(false);
            setShowPostOpCropper(true);
          }}
        />
      }
      {!isIntraOpFormUploading && isTouchDevice && showIntraOpFormCamera &&
        <CameraContainer
          images={intraOpData.images}
          onSkip={() => { setShowIntraOpFormCamera(false); }}
          onCancel={() => {
            setShowIntraOpFormCamera(false);
            setShowIntraOpCropper(false);
          }}
          onScan={(img) => {
            if (intraOpData.isReScan) {
              setIntraOpData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.images.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setIntraOpData((prvState) => ({
                ...prvState,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowIntraOpFormCamera(false);
            setShowIntraOpCropper(true);
          }}
        />
      }
      {!isUploadingFaceSheet && isTouchDevice && showFaceSheetCamera &&
        <CameraContainer
          images={faceSheetData.images}
          onSkip={() => { setShowFaceSheetCamera(false); }}
          onEdit={() => {
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(true);
          }}
          onCancel={() => {
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(false);
          }}
          onScan={(img) => {
            if (faceSheetData.isReScan) {
              setFaceSheetData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setFaceSheetData((prvState) => ({
                ...prvState,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(true);
          }}
        />
      }
      {/* Consent Form Cropper */}
      {!isConsentFormUploading && isTouchDevice && showConsentFormCropper &&
        <CropperContainer
          title='Consent Form'
          ref={consentFormCropper}
          keySuffix='img_consent_form'
          editMode={consentFormCropperEditMode}
          images={consentFormData.images}
          croppedImages={consentFormData.croppedImages}
          currentIndex={Math.min(consentFormData.activeIndex, consentFormData.images.length - 1)}
          onPrint={handleOnClickViewConsentForm}
          onDownload={handleOnClickViewConsentForm}
          loadImage={(idx) => {
            setConsentFormData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setConsentFormData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                activeIndex: images.length - 1
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = consentFormData.croppedImages.concat();
            tmpCroppedImages[idx] = consentFormData.images[idx];
            setConsentFormData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={() => {
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(false);
          }}
          onCancel={() => {
            // setConsentFormData((prvState) => ({ images: [], croppedImages: [], activeIndex: 0, isReScan: false }))
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(false);
          }}
          onReScan={() => {
            setConsentFormData(prvState => ({ ...prvState, isReScan: true }));
            setShowConsentFormCamera(true);
          }}
          onScanNext={() => {
            setShowConsentFormCamera(true);
            setShowConsentFormCropper(false);
            setConsentFormData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (consentFormData.isReScan) {
              setConsentFormData(prvState => {
                const { cropper } = consentFormCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setConsentFormData(prvState => {
                const { cropper } = consentFormCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                  // croppedImages: prvState.croppedImages.concat(cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0))
                };
              });
            }
          }}
          onFinish={() => {
            handleOnConsentFormCropAccept();
          }}
        />
      }
      {/* PreOp Form Cropper */}
      {!isPreOpFormUploading && isTouchDevice && showPreOpCropper &&
        <CropperContainer
          title='Pre/Post Op Form(s)'
          ref={preOpCropper}
          keySuffix='img_preOp_form'
          editMode={preOpCropperEditMode}
          images={preOpData.images}
          croppedImages={preOpData.croppedImages}
          currentIndex={Math.min(preOpData.activeIndex, preOpData.images.length - 1)}
          onPrint={handleOnClickViewPreOpForm}
          onDownlaod={handleOnClickViewPreOpForm}
          loadImage={(idx) => {
            setPreOpData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setPreOpData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                activeIndex: images.length - 1
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = preOpData.croppedImages.concat();
            tmpCroppedImages[idx] = preOpData.images[idx];
            setPreOpData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={() => {
            setShowPreOpFormCamera(false);
            setShowPreOpCropper(false);
          }}
          onCancel={() => {
            // setPreOpData(prvState => ({ images: [], croppedImages: [], activeIndex: 0, isReScan: false }));
            setShowPreOpFormCamera(false);
            setShowPreOpCropper(false);
          }}
          onReScan={() => {
            setPreOpData(prvState => ({ ...prvState, isReScan: true }));
            setShowPreOpFormCamera(true);
          }}
          onScanNext={() => {
            setShowPreOpFormCamera(true);
            setShowPreOpCropper(false);
            setPreOpData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (preOpData.isReScan) {
              setPreOpData(prvState => {
                const { cropper } = preOpCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setPreOpData(prvState => {
                const { cropper } = preOpCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                  // croppedImages: prvState.croppedImages.concat(cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0))
                };
              });
            }
          }}
          onFinish={() => { handleOnPreOpFormCropAccept(); }}
        />
      }
      {/* PostOp Form Cropper */}
      {!isPostOpFormUploading && isTouchDevice && showPostOpCropper &&
        <CropperContainer
          title='Pre/Post Op Form(s)'
          ref={postOpCropper}
          // src={postOpImage.length > 0 ? postOpImage[postOpImage.length - 1] : null}
          // src={postOpData.images[postOpData.activeIndex]}
          images={postOpData.images}
          croppedImages={postOpData.croppedImages}
          currentIndex={Math.min(postOpData.activeIndex, postOpData.images.length - 1)}
          keySuffix='img_postOp_form'
          loadImage={(idx) => {
            setPostOpData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = postOpData.croppedImages.concat();
            tmpCroppedImages[idx] = postOpData.images[idx];
            setPostOpData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={() => {
            setShowPostOpFormCamera(false);
            setShowPostOpCropper(false);
          }}
          onCancel={() => {
            // setPostOpData(prvState => ({ images: [], croppedImages: [], activeIndex: 0, isReScan: false }));
            setShowPostOpFormCamera(false);
            setShowPostOpCropper(false);
          }}
          onReScan={() => {
            setPostOpData(prvState => ({ ...prvState, isReScan: true }));
            setShowPostOpFormCamera(true);
          }}
          onScanNext={() => {
            setShowPostOpFormCamera(true);
            setShowPostOpCropper(false);
            setPostOpData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (postOpData.isReScan) {
              setPostOpData(prvState => {
                const { cropper } = postOpCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setPostOpData(prvState => {
                const { cropper } = postOpCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                  // croppedImages: prvState.croppedImages.concat(cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0))
                };
              });
            }
          }}
          onFinish={() => { handleOnPostOpFormCropAccept(); }}
        />
      }
      {/* IntraOp Form Cropper */}
      {!isIntraOpFormUploading && isTouchDevice && showIntraOpCropper &&
        <CropperContainer
          title='IntraOp Form'
          ref={intraOpCropper}
          keySuffix='img_intraOp_form'
          editMode={intraOpCropperEditMode}
          images={intraOpData.images}
          croppedImages={intraOpData.croppedImages}
          currentIndex={Math.min(intraOpData.activeIndex, intraOpData.images.length - 1)}
          onPrint={handleOnClickViewIntraOpForm}
          onDownload={handleOnClickViewIntraOpForm}
          loadImage={(idx) => {
            setIntraOpData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setIntraOpData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                activeIndex: images.length - 1
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = intraOpData.croppedImages.concat();
            tmpCroppedImages[idx] = intraOpData.images[idx];
            setIntraOpData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={() => {
            setShowIntraOpFormCamera(false);
            setShowIntraOpCropper(false);
          }}
          onCancel={() => {
            // setIntraOpData(prvState => ({ images: [], croppedImages: [], activeIndex: 0, isReScan: false }));
            setShowIntraOpFormCamera(false);
            setShowIntraOpCropper(false);
          }}
          onReScan={() => {
            setIntraOpData(prvState => ({ ...prvState, isReScan: true }));
            setShowIntraOpFormCamera(true);
          }}
          onScanNext={() => {
            setShowIntraOpCropper(false);
            setIntraOpData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
            setShowIntraOpFormCamera(true);
          }}
          onCrop={() => {
            if (intraOpData.isReScan) {
              setIntraOpData(prvState => {
                const { cropper } = intraOpCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setIntraOpData(prvState => {
                const { cropper } = intraOpCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                  // croppedImages: prvState.croppedImages.concat(cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0))
                };
              });
            }
          }}
          onFinish={() => { handleOnIntraOpFormCropAccept(); }}
        />
      }
      {/* FaceSheet Cropper */}
      {!isUploadingFaceSheet && isTouchDevice && showFaceSheetCropper &&
        <CropperContainer
          title='Face Sheet'
          ref={faceSheetCropper}
          keySuffix='img_faceSheet_form'
          editMode={faceSheetCropperEditMode}
          images={faceSheetData.images}
          croppedImages={faceSheetData.croppedImages}
          currentIndex={Math.min(faceSheetData.activeIndex, faceSheetData.images.length - 1)}
          onPrint={() => { handleOnClickViewFaceSheet(); }}
          onDownlaod={() => { handleOnClickFaceSheetDownload(); }}
          loadImage={(idx) => {
            setFaceSheetData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setFaceSheetData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                activeIndex: images.length - 1
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = faceSheetData.croppedImages.concat();
            tmpCroppedImages[idx] = faceSheetData.images[idx];
            setFaceSheetData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={() => {
            // setFaceSheetData(prvState => ({ images: [], croppedImages: [], activeIndex: 0, isReScan: false }));
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(false);
          }}
          onCancel={() => {
            // setFaceSheetData(prvState => ({ images: [], croppedImages: [], activeIndex: 0, isReScan: false }));
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(false);
          }}
          onReScan={() => {
            setFaceSheetData(prvState => ({ ...prvState, isReScan: true }));
            setShowFaceSheetCamera(true);
          }}
          onScanNext={() => {
            setShowFaceSheetCamera(true);
            setShowFaceSheetCropper(false);
            setFaceSheetData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (faceSheetData.isReScan) {
              setFaceSheetData(prvState => {
                const { cropper } = faceSheetCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setFaceSheetData(prvState => {
                const { cropper } = faceSheetCropper.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                return {
                  ...prvState,
                  croppedImages: tmpCroppedImages
                  // croppedImages: prvState.croppedImages.concat(cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0))
                };
              });
            }
          }}
          onFinish={() => { handleOnFaceSheetFormCropAccept(); }}
        />
      }
      {/* Uploading file */}
      {isUploading &&
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'yellow',
            opacity: '0.5'
          }}
        >
          Uploading File
        </div>
      }
      {savingCaseForm &&
        <Loader
          iconType="cog"
          iconSize="2x"
          text="Saving Case Form"
          style={{
            display: 'flex',
            flexFlow: 'row',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        />
      }
      {!showConsentFormCamera && !showPreOpFormCamera && !showPostOpFormCamera && !showIntraOpFormCamera &&
        !showConsentFormCropper && !showFaceSheetCropper && !showPreOpCropper && !showPostOpCropper && !showIntraOpCropper &&
        <div className={css(styles.drawerContainer)}>
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div
              style={{
                position: 'absolute',
                top: '1px',
                right: '1px',
                height: '20px',
                width: '20px',
                background: salmon[500],
                color: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => {
                props.onClose(isCaseDataUpdated);
              }}
            >
              <i className='fa fa-lg fa-times' />
            </div>
            {/* Facility */}
            <div className='fx'>
              <div className={css(styles.headerSection)}>Facility</div>
              <div className={css(styles.headerSectionText)}>
                { facilityName}
              </div>
            </div>
            {/* Patient */}
            <div className='fx'>
              <div className={css(styles.headerSection)}>Pt Name</div>
              <div className={`fx ${css(styles.headerSectionText)}`}>
                {/* {`${patLastName.toLowerCase()}, ${patFirstName.toLowerCase()} (${Math.round(patientAge)}y${patSex}, ${new Date(Date.parse(patDob)).toLocaleDateString()})`} */}
                {patFirstName.length > 0 && patLastName.length > 0 ? patientSummary : ''}
              </div>
            </div>
            {/* Surgeons */}
            <div className='fx'>
              <div className={css(styles.headerSection)}>Surgeon</div>
              <div className={css(styles.headerSectionText)}>{surgeonsNames}</div>
            </div>
            {/* Procedure */}
            <div className='fx'>
              <div className={css(styles.headerSection)}>Procedure</div>
              <div className={css(styles.headerSectionText)}>{procedureNames}</div>
            </div>
            {/* DOS and In/Out Pt */}
            <div className='fx'>
              <div className={css(styles.headerSection)}>DOS</div>
              <div className={`fx ${css(styles.headerSectionText)}`}>
                <div>
                  <DatePicker
                    left={-42}
                    right={0}
                    date={dosDate}
                    month={dosMonth}
                    year={dosYear}
                    isPrisitine={false}
                    onChangeDate={(year, month, date) => {
                      setDosYear(year);
                      setDosMonth(month);
                      setDosDate(date);
                    }}
                  />
                </div>
                <div className='fx'>
                  <div className='ml-4'>
                    <RadioButton
                      text='OutPt'
                      isSelected={patientType === 'OutPt'}
                      onSelectionChanged={handleOnClickOutPt}
                    />
                  </div>
                  <div className='ml-4'>
                    <RadioButton
                      text='InPt'
                      isSelected={patientType === 'InPt'}
                      onSelectionChanged={handleOnClickInPt}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Tabs */}
          <div className={css(styles.tabs)}>
            <TabsContainer
              dropDownId="d1"
              showPlusIcon={false}
              containerWidth={359}
              noDropdown={true}
              shiftMethod='minWidth'
              otherElementsWidth={0}
              minWidth={358}
              dropDownTop={39}
              dropDownLeft={44}
              dropDownHeaderStyles={{
                minWidth: '170px',
                maxWidth: '370px',
                width: '100%'
              }}
              containerStyles={{
                width: 'calc(100% - 320px)'
              }}
            >
              {/* Face sheet */}
              <TabItem
                tabKey="1"
                key='9898'
                parentKey="0111"
                showTabIcon={false}
                text="Info"
                isActive={activeTab === 0}
                style={{
                  // borderLeft: '1px solid #082840',
                  paddingRight: '0',
                  width: '84px',
                  minWidth: '84px',
                  maxWidth: '84px'
                }}
                textStyles={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                onActivate={() => {
                  setActiveTab(0);
                }}
              >
                <div>Face Sheet </div>
              </TabItem>
              {/* Anesthesia Forms */}
              <TabItem
                tabKey="3"
                key='9901'
                parentKey="0111"
                text="Documents"
                isActive={activeTab === 1}
                showTabIcon={false}
                style={{
                  // borderLeft: '1px solid #082840',
                  paddingRight: '0',
                  width: '120px',
                  minWidth: '120px',
                  maxWidth: '120px'
                }}
                textStyles={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                onActivate={() => {
                  setActiveTab(1);
                }}
              >
                <div>
                  Anesthesia Forms
                </div>
              </TabItem>
              {/* Case Info */}
              <TabItem
                tabKey="2"
                key='9899'
                parentKey="0111"
                text="Info"
                isActive={activeTab === 2}
                showTabIcon={false}
                style={{ paddingRight: '0', width: '93px', minWidth: '93px', maxWidth: '93px' }}
                textStyles={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                onActivate={() => { setActiveTab(2); }}
              >
                <div>
                  Case Details
                </div>
              </TabItem>
              {/* Notes Tab */}
              {/* <TabItem
                tabKey="4"
                key='9902'
                parentKey="0111"
                text="Summary"
                isActive={activeTab === 3}
                showTabIcon={false}
                style={{ paddingRight: '0', width: '54px', minWidth: '54px', maxWidth: '54px' }}
                textStyles={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                onActivate={() => { setActiveTab(3); }}
              >
                <div>Notes</div>
              </TabItem> */}
            </TabsContainer>
          </div>
          {/* Active tab component */}
          <div
            style={{
              // height: 'calc(100vh - 132px)',
              height: 'calc(100% - 132px)',
              // height: `${window.innerHeight - 132}px`,
              overflow: 'scroll'
              // position: 'relative'
            }}
          >
            {activeComp}
          </div>
          {/* Footer */}
          <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
            {/* Cancel button */}
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              isOutlineBtn
              onClick={() => {
                props.onClose(isCaseDataUpdated);
              }}
              style={{
                height: '32px',
                width: '69px',
                padding: '3px 0'
              }}
            >
              Cancel
            </Button>
            {/* Save button */}
            <Button
              btnType="primary"
              className="ml-2 fx fx__hv-center"
              // isDisabled={!canSaveCase}
              onClick={handleOnClickSaveCase}
              style={{
                height: '32px',
                width: '69px',
                padding: '3px 0'
              }}
            >
              {`${props.editMode ? 'Update' : 'Save'}`}
            </Button>
          </div>
        </div>
      }
    </Drawer>
  );
};

export default CaseDrawer;
